import React, { useContext, useEffect, useState } from "react";
import dayjs from "dayjs";
import styles from "../../form.module.css";
import {
  Button,
  Checkbox,
  Col,
  Flex,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Space,
  Typography,
  DatePicker,
  Table,
  TableProps,
  Image,
} from "antd";
import useFetch from "../../../../hooks/useFetch";
import useToast from "../../../../hooks/useToast";
import {
  eFORMSTATUS,
  eResultCode,
  eSTAGE,
  eYES_OR_NO,
  eUSER_FORM_TYPE,
  INPUT_LENGTH,
} from "../../../../utils/enum";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import {
  ExclamationCircleOutlined,
  LoadingOutlined,
  PlusOutlined,
  FilePdfOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { LanguageContext } from "../../../../context/language-context";
import { FaCheck } from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserContext } from "../../../../context";
import { GetFormData, updateField } from "../../../../utils/api.constant";
import { TFormProps } from "../../../../types";
import { LoaderContext } from "../../../../context/loader";
import EncryptUtils from "../../../../utils/encrypt";
import Utils from "../../../../utils";
import {
  allowOnlyNumericInput_onKeyDown,
  disableFutureDates_on_input_date_max,
  disableFutureDates_on_input_month_max,
  numberInputRules,
  validateFile,
} from "../../../../utils/input.validator";
import { getNumberInLanguage } from "../../../../utils/helperFunctions";
import {
  DocumentUploadTableField,
  DocumentUploadField,
} from "../../../../components/document-upload";
import { CURRENT_YEAR, EXAM_INSTITUTE_LIST } from "../../../../utils/constants";
import { Rule } from "antd/es/form";
import { useFormAutoSave } from "../../../../hooks/useFormAutoSave";
import { eAnswerType } from "../../../../utils/enum";
import FormHelpTooltip from "../../../../components/common/FormHelpTooltip";

const formItemLayout = {
  labelCol: {
    span: 24,
  },
  wrapperCol: {
    span: 24,
  },
};

const TeacherPartTwo = ({
  disabled = false,
  currentFormId,
  currentUserId,
  currentFormFor,
}: TFormProps) => {
  const [form] = Form.useForm();
  const contextForLoader = useContext(LoaderContext);
  const { isLoading, setIsLoading } = contextForLoader as any;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const navigate = useNavigate();
  const [formId, setFormId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | number | null>(null);
  const [formFor, setFormFor] = useState<string | null>(null);
  const { user } = useContext(UserContext) as any;
  const { dictionary } = useContext(LanguageContext) as any;

  // Custom Text function bound to this component's dictionary to prevent hook call errors in nested maps
  const Text = ({ tid, def }: { tid: string; def?: string }) => {
    return dictionary?.[tid] || def || "";
  };

  const [documentsData, setDocumentsData] = useState<any>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [applyPdfStyles, setApplyPdfStyles] = useState(false);

  const affidavitAccepted = Form.useWatch("affidavitAccepted", form);
  const institutionAffidavitAccepted = Form.useWatch(
    "institutionAffidavitAccepted",
    form,
  );

  const uploadButton = (
    <button style={{ border: 0, background: "none" }} type="button">
      {isLoading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>Upload</div>
    </button>
  );

  const DROPDOWN_PLACEHOLDER = Text({
    tid: "DROPDOWN_PLACEHOLDER",
    def: "DROPDOWN_PLACEHOLDER",
  });

  const REQUIRED = Text({ tid: "REQUIRED", def: "Required" });
  const SELECT_DATE = Text({ tid: "SELECT_DATE", def: "Select Date" });
  const ATTACHED_DOC =
    Text({ tid: "ATTACHED_DOCUMENT", def: "Attached Document" }) + ":";
  const PREVIEW = Text({ tid: "PREVIEW", def: "Preview" });
  const REMOVE = Text({ tid: "REMOVE", def: "Remove" });
  const ADD = Text({ tid: "ADD", def: "Add" });
  const SELECT_YEAR = Text({ tid: "SELECT_YEAR", def: "Select Year" });
  const FIRST = Text({ tid: "FIRST", def: "First" });
  const SECOND = Text({ tid: "SECOND", def: "Second" });
  const THIRD = Text({ tid: "THIRD", def: "Third" });
  const EXAM_PENDING = Text({ tid: "EXAM_PENDING", def: "Exam pending" });
  const EXAM_NOT_TAKEN = Text({ tid: "EXAM_NOT_TAKEN", def: "Exam not taken" });

  const q4_data = Form.useWatch("TEACHER_2026_PT2_Q4", form);
  const q5_data = Form.useWatch("TEACHER_2026_PT2_Q5", form);

  const isOtherInstitute = (index: number) => {
    const selectedInstituteOther = q4_data?.[index]?.TEACHER_2026_PT2_Q4_D;
    return selectedInstituteOther === "INSTITUTE_OTHER";
  };

  const isOtherSubject = (index: number) => {
    const selectedSubjectOther = q5_data?.[index]?.TEACHER_2026_PT2_Q5_A;
    return selectedSubjectOther === "SUBJECT_OTHER";
  };

  useEffect(() => {
    const fieldsToInitialize = [
      "TEACHER_2026_PT2_Q4",
      "TEACHER_2026_PT2_Q5",
      "TEACHER_2026_PT2_Q6",
      "TEACHER_2026_PT2_Q8",
      "TEACHER_2026_PT2_Q9",
      "TEACHER_2026_PT2_Q10",
      "TEACHER_2026_PT2_Q11",
      "TEACHER_2026_PT2_Q12",
      "TEACHER_2026_PT2_Q13",
      "TEACHER_2026_PT2_Q14",
    ];

    fieldsToInitialize.forEach((field) => {
      if (
        !form.getFieldValue(field) ||
        form.getFieldValue(field).length === 0
      ) {
        form.setFieldsValue({ [field]: [{}] });
      }
    });

    if (
      !form.getFieldValue("TEACHER_2026_PT2_Q7") ||
      form.getFieldValue("TEACHER_2026_PT2_Q7").length === 0
    ) {
      const Q7_Rows = [2025, 2024, 2023, 2022, 2021].map((yr) => ({
        year: yr.toString(),
        mulant: "",
        level1: "",
        level2: "",
        other: "",
        total: "",
      }));
      form.setFieldsValue({ TEACHER_2026_PT2_Q7: Q7_Rows });
    }
  }, []);

  useEffect(() => {
    if (searchParams) {
      const fId = EncryptUtils.decrypt(
        searchParams.get("id") || searchParams.get("id2") || "",
      );
      const uId = EncryptUtils.decrypt(
        searchParams.get("ui") || searchParams.get("ufmid") || "",
      );
      const fFor = EncryptUtils.decrypt(searchParams.get("ff") || "");
      setFormId(fId);
      setUserId(uId);
      setFormFor(fFor);
    }
    if (currentFormId) {
      setFormId(EncryptUtils.decrypt(currentFormId));
    }
    if (currentUserId) {
      setUserId(EncryptUtils.decrypt(currentUserId));
    }
    if (currentFormFor) {
      setFormFor(EncryptUtils.decrypt(currentFormFor));
    }
  }, [searchParams, currentFormId, currentUserId, currentFormFor]);

  const buildArrayPayload = (
    currentData: any[],
    fieldsToStrip: string[] = [],
    patch?: { rowIndex: number; fieldKey: string; value: any },
  ): string => {
    return JSON.stringify(
      (currentData || []).map((item: any, i: number) => {
        const rawRow =
          patch && i === patch.rowIndex
            ? { ...item, [patch.fieldKey]: patch.value }
            : { ...item };

        const row = { ...rawRow };
        Object.keys(row).forEach((key) => {
          const val = row[key];
          if (dayjs.isDayjs(val)) {
            if (
              [
                "TEACHER_2026_PT2_Q4_B",
                "TEACHER_2026_PT2_Q5_D",
                "TEACHER_2026_PT2_Q6_B",
                "TEACHER_2026_PT2_Q6_C",
                "TEACHER_2026_PT2_Q8_D",
                "TEACHER_2026_PT2_Q9_C",
                "TEACHER_2026_PT2_Q10_B",
              ].includes(key)
            ) {
              row[key] = val.format("MM/YYYY");
            } else if (
              [
                "TEACHER_2026_PT2_Q8_D",
                "TEACHER_2026_PT2_Q11_C",
                "TEACHER_2026_PT2_Q12_C",
                "TEACHER_2026_PT2_Q13_C",
                "TEACHER_2026_PT2_Q14_D",
              ].includes(key)
            ) {
              row[key] = val.format("YYYY");
            } else {
              row[key] = val.format("YYYY-MM-DD");
            }
          }
        });

        fieldsToStrip.forEach((f) => {
          row[f] = "";
        });

        return row;
      }),
    );
  };

  const handleFieldChange = async (
    field: string,
    value: any,
    answerType: number = eAnswerType.TEXT,
    bulkPayload?: any[],
    isSubmitted?: boolean,
  ) => {
    if (formId?.length) {
      try {
        setIsSubmitting(true);

        const answers: any[] = [];

        if (field.length > 0) {
          const trimmedValue = typeof value === "string" ? value.trim() : value;
          if (trimmedValue === null || trimmedValue === undefined) {
            form.setFieldValue(field, "");
          }

          let detectedType = answerType;
          if (typeof trimmedValue === "string") {
            if (trimmedValue.startsWith("[")) {
              detectedType = eAnswerType.ARRAY;
            } else if (trimmedValue.startsWith("{")) {
              detectedType = eAnswerType.OBJECT;
            }
          } else if (Array.isArray(trimmedValue)) {
            detectedType = eAnswerType.ARRAY;
          }

          let formattedAnswer = trimmedValue;

          if (Array.isArray(trimmedValue)) {
            formattedAnswer = buildArrayPayload(trimmedValue);
          } else if (
            detectedType === eAnswerType.ARRAY ||
            detectedType === eAnswerType.OBJECT
          ) {
            formattedAnswer =
              typeof trimmedValue === "string"
                ? trimmedValue
                : JSON.stringify(trimmedValue);
          } else if (
            detectedType === eAnswerType.DATE &&
            typeof trimmedValue === "object" &&
            trimmedValue?.format
          ) {
            formattedAnswer = trimmedValue.format("YYYY-MM-DD");
          }

          answers.push({
            questionKey: field,
            answerType: detectedType,
            answer: formattedAnswer,
            part: eSTAGE.STAGE2,
          });
        }

        if (bulkPayload && Array.isArray(bulkPayload)) {
          answers.push(...bulkPayload);
        }

        const payload: any = {
          data: {
            id: formId,
            answers: answers,
          },
        };

        if (isSubmitted) {
          payload.data.isSubmitted = true;
        }

        const response = await post(updateField, payload);
        const { dataResponse } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode == eResultCode.SUCCESS) {
          if (isSubmitted) {
            onShowToast({
              type: ToastType.success,
              title: <FaCheck />,
              position: ToastOpen.leftBottom,
              content: "Form Submitted Successfully!",
            });
            setTimeout(() => {
              navigate("/");
            }, 1000);
          }
        } else if (returnCode == eResultCode.INVALID_REQUEST) {
          onShowToast({
            type: ToastType.error,
            title: <ExclamationCircleOutlined />,
            position: ToastOpen.leftBottom,
            content: description,
          });
        }
      } catch (error) {
        console.error("Teacher Form 2 Error: ", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const getFormData = async () => {
    setIsLoading(true);
    try {
      const payload: any = {
        data: {
          id: formId,
          part: eSTAGE.STAGE2,
          isPtOne: false,
          year: CURRENT_YEAR,
          userFormType: eUSER_FORM_TYPE.TEACHER,
          userId: userId,
          formFor: formFor,
        },
      };

      const response = await post(GetFormData, payload);
      const { dataResponse, data } = response;
      const { returnCode, description } = dataResponse;
      if (returnCode == eResultCode.SUCCESS && Array.isArray(data)) {
        const formattedData: { [key: string]: any } = {};
        const docs: any[] = [];
        const DATE_FIELD_KEYS: string[] = ["TEACHER_2026_PT2_Q21"];

        data.forEach((answer: any) => {
          const { questionKey, answerType, answer: answerValue } = answer;

          if (!questionKey) return;

          switch (answerType) {
            case eAnswerType.ARRAY:
            case eAnswerType.OBJECT:
              try {
                formattedData[questionKey] = answerValue
                  ? JSON.parse(answerValue)
                  : answerType === eAnswerType.ARRAY
                    ? []
                    : {};
              } catch (error) {
                formattedData[questionKey] =
                  answerType === eAnswerType.ARRAY ? [] : {};
              }
              break;
            case eAnswerType.FILE:
              formattedData[questionKey] = answerValue || "";
              if (answerValue) {
                docs.push({
                  docPath: answerValue,
                  labelName: questionKey,
                });
              }
              break;
            case eAnswerType.DATE:
              formattedData[questionKey] = Utils.parseToDayjs(answerValue);
              break;
            case eAnswerType.TEXT:
            default:
              formattedData[questionKey] = answerValue || "";
              if (DATE_FIELD_KEYS.includes(questionKey) && answerValue) {
                formattedData[questionKey] = Utils.parseToDayjs(answerValue);
              }
              break;
          }
        });

        const arrayDateFields: Record<string, string[]> = {
          TEACHER_2026_PT2_Q4: ["TEACHER_2026_PT2_Q4_B"],
          TEACHER_2026_PT2_Q5: ["TEACHER_2026_PT2_Q5_D"],
          TEACHER_2026_PT2_Q6: [
            "TEACHER_2026_PT2_Q6_B",
            "TEACHER_2026_PT2_Q6_C",
          ],
          TEACHER_2026_PT2_Q8: ["TEACHER_2026_PT2_Q8_D"],
          TEACHER_2026_PT2_Q9: ["TEACHER_2026_PT2_Q9_C"],
          TEACHER_2026_PT2_Q10: ["TEACHER_2026_PT2_Q10_B"],
          TEACHER_2026_PT2_Q11: ["TEACHER_2026_PT2_Q11_C"],
          TEACHER_2026_PT2_Q12: ["TEACHER_2026_PT2_Q12_C"],
          TEACHER_2026_PT2_Q13: ["TEACHER_2026_PT2_Q13_C"],
          TEACHER_2026_PT2_Q14: ["TEACHER_2026_PT2_Q14_D"],
        };

        Object.keys(arrayDateFields).forEach((tableKey) => {
          if (
            Array.isArray(formattedData[tableKey]) &&
            formattedData[tableKey].length > 0
          ) {
            formattedData[tableKey] = formattedData[tableKey].map(
              (row: any) => {
                if (!row) return row; // Safe check for null rows
                const newRow = { ...row };
                arrayDateFields[tableKey].forEach((dateKey) => {
                  const val = newRow[dateKey];
                  if (val) {
                    newRow[dateKey] = Utils.parseToDayjs(val);
                  }
                });
                return newRow;
              },
            );
          }
        });

        if (docs.length > 0) {
          setDocumentsData(docs);
        }

        if (Object.keys(formattedData).length > 0) {
          form.setFieldsValue(sanitizeFormData(formattedData));
          setTimeout(() => {
            const uploadedDocuments = getUploadedDocuments();
            form.setFieldsValue({
              TEACHER_2026_PT2_Q15: uploadedDocuments,
            });
          }, 100);

          if (
            formattedData.affidavitAccepted ||
            formattedData.affidavitAccepted === "1"
          ) {
            form.setFieldValue("affidavitAccepted", true);
          }
          if (
            formattedData.institutionAffidavitAccepted ||
            formattedData.institutionAffidavitAccepted === "1"
          ) {
            form.setFieldValue("institutionAffidavitAccepted", true);
          }
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // === GLOBAL DATE SANITIZER ===
  // Prevents date.isValid crashes by ensuring all DatePicker fields have valid dayjs or null
  const DATE_FIELD_SUFFIXES = new Set([
    "TEACHER_2026_PT2_Q4_B",
    "TEACHER_2026_PT2_Q5_D",
    "TEACHER_2026_PT2_Q6_B",
    "TEACHER_2026_PT2_Q6_C",
    "TEACHER_2026_PT2_Q8_D",
    "TEACHER_2026_PT2_Q9_C",
    "TEACHER_2026_PT2_Q10_B",
    "TEACHER_2026_PT2_Q11_C",
    "TEACHER_2026_PT2_Q12_C",
    "TEACHER_2026_PT2_Q13_C",
    "TEACHER_2026_PT2_Q14_D",
    "TEACHER_2026_PT2_Q21",
    "TEACHER_2026_PT2_Q22",
  ]);

  const sanitizeFormData = (data: Record<string, any>): Record<string, any> => {
    const sanitized: Record<string, any> = {};
    for (const [key, value] of Object.entries(data)) {
      if (DATE_FIELD_SUFFIXES.has(key)) {
        // Top-level date field: must be dayjs or null
        if (dayjs.isDayjs(value) && value.isValid()) {
          sanitized[key] = value;
        } else if (typeof value === "string" && value) {
          sanitized[key] = Utils.parseToDayjs(value);
        } else {
          sanitized[key] = null;
        }
      } else if (Array.isArray(value)) {
        // Array (Form.List): recursively sanitize each row
        sanitized[key] = value.map((row: any) => {
          if (!row || typeof row !== "object") return row;
          const sanitizedRow: any = {};
          for (const [rk, rv] of Object.entries(row)) {
            if (DATE_FIELD_SUFFIXES.has(rk)) {
              if (dayjs.isDayjs(rv) && (rv as any).isValid()) {
                sanitizedRow[rk] = rv;
              } else if (typeof rv === "string" && rv) {
                sanitizedRow[rk] = Utils.parseToDayjs(rv);
              } else {
                sanitizedRow[rk] = null;
              }
            } else {
              sanitizedRow[rk] = rv;
            }
          }
          return sanitizedRow;
        });
      } else {
        sanitized[key] = value;
      }
    }
    return sanitized;
  };

  const getMappedData = (data: any) => {
    if (!data) return {};

    const defaultQ7 = [
      { TEACHER_2026_PT2_Q7_A: "2025" },
      { TEACHER_2026_PT2_Q7_A: "2024" },
      { TEACHER_2026_PT2_Q7_A: "2023" },
      { TEACHER_2026_PT2_Q7_A: "2022" },
      { TEACHER_2026_PT2_Q7_A: "2021" },
    ];

    // Helper: map an array of objects with custom keys to 2026 prefixed keys
    const mapArrayWithCustomKeys = (
      sourceArr: any[],
      keyMap: Record<string, string>, // { "2026_suffix": "2025_json_key" }
      prefixTo: string,
      dateKeys: string[] = [],
    ): any[] => {
      if (!Array.isArray(sourceArr) || sourceArr.length === 0) {
        const emptyItem: any = {};
        Object.keys(keyMap).forEach((k) => {
          emptyItem[`${prefixTo}${k}`] = dateKeys.includes(k) ? null : "";
        });
        return [emptyItem];
      }
      return sourceArr.map((item) => {
        if (!item) return {};
        const newItem: any = {};
        Object.entries(keyMap).forEach(([toSuffix, fromKey]) => {
          const val =
            item[fromKey] !== undefined && item[fromKey] !== null
              ? item[fromKey]
              : "";
          if (dateKeys.includes(toSuffix)) {
            newItem[`${prefixTo}${toSuffix}`] =
              val && val !== "Invalid Date" ? Utils.parseToDayjs(val) : null;
          } else {
            newItem[`${prefixTo}${toSuffix}`] = val || "";
          }
        });
        return newItem;
      });
    };

    // --- Q4: 2025 has FLAT fields (Q4_A, Q4_B, Q4_C, Q4_D), wrap into array for 2026 Form.List ---
    const q4Mapped = data.TEACHER_PT2_Q4_A
      ? [
          {
            TEACHER_2026_PT2_Q4_A: data.TEACHER_PT2_Q4_A || "",
            TEACHER_2026_PT2_Q4_B: data.TEACHER_PT2_Q4_B
              ? Utils.parseToDayjs(data.TEACHER_PT2_Q4_B)
              : null,
            TEACHER_2026_PT2_Q4_C: data.TEACHER_PT2_Q4_C || "",
            TEACHER_2026_PT2_Q4_D: data.TEACHER_PT2_Q4_D || "",
            TEACHER_2026_PT2_Q4_E: "",
          },
        ]
      : [
          {
            TEACHER_2026_PT2_Q4_A: "",
            TEACHER_2026_PT2_Q4_B: null,
            TEACHER_2026_PT2_Q4_C: "",
            TEACHER_2026_PT2_Q4_D: "",
            TEACHER_2026_PT2_Q4_E: "",
          },
        ];

    // --- Q5: 2025 Q5 is student counts (year/mulant/level1/level2/other/total) -> maps to 2026 Q7, NOT Q5 ---
    // Q5 in 2026 is certificates - no direct mapping from 2025
    const q5Mapped = [
      {
        TEACHER_2026_PT2_Q5_A: "",
        TEACHER_2026_PT2_Q5_B: "",
        TEACHER_2026_PT2_Q5_C: "",
        TEACHER_2026_PT2_Q5_D: null,
        TEACHER_2026_PT2_Q5_E: "",
        TEACHER_2026_PT2_Q5_F: "",
        TEACHER_2026_PT2_Q5_G: "",
      },
    ];

    // --- Q7: Map 2025 Q5 student counts into 2026 Q7 student count table ---
    let q7Mapped = defaultQ7;
    if (
      Array.isArray(data.TEACHER_PT2_Q5) &&
      data.TEACHER_PT2_Q5.length === 5
    ) {
      q7Mapped = data.TEACHER_PT2_Q5.map((item: any) => ({
        TEACHER_2026_PT2_Q7_A:
          item.year && item.year !== "Invalid Date" ? item.year : "",
        TEACHER_2026_PT2_Q7_B: item.mulant || "",
        TEACHER_2026_PT2_Q7_C: item.level1 || "",
        TEACHER_2026_PT2_Q7_D: item.level2 || "",
        TEACHER_2026_PT2_Q7_E: item.other || "",
        TEACHER_2026_PT2_Q7_F: item.total || "",
      }));
    }

    // --- Q9: Conference/Assembly ---
    const q9Mapped = mapArrayWithCustomKeys(
      data.TEACHER_PT2_Q14,
      {
        A: "nameOfVedicStudent",
        B: "age",
        C: "year",
        D: "currentLevelOfEducation",
      },
      "TEACHER_2026_PT2_Q9_",
      ["C"],
    );

    // --- Q10: Yagna Work ---
    const q10Mapped = mapArrayWithCustomKeys(
      data.TEACHER_PT2_Q15,
      {
        A: "nameOfVedicStudent",
        B: "year",
        C: "currentlyTeachingVedaInstitution",
      },
      "TEACHER_2026_PT2_Q10_",
      ["B"],
    );

    // --- Q11: Awards ---
    const q11Mapped = mapArrayWithCustomKeys(
      data.TEACHER_PT2_Q9,
      {
        A: "Conference_or_Meeting",
        B: "Organizer",
        C: "year",
        DOC: "TEACHER_PT2_Q9_DOC",
      },
      "TEACHER_2026_PT2_Q11_",
      ["C"],
    );

    // --- Q12: Publications ---
    // 2026 form: Q12_A=title, Q12_B=publisher, Q12_C=year(DatePicker), Q12_DOC=doc
    const q12Mapped = mapArrayWithCustomKeys(
      data.TEACHER_PT2_Q10,
      {
        A: "yagyaWork",
        B: "place",
        C: "year",
        DOC: "TEACHER_PT2_Q10_DOC",
      },
      "TEACHER_2026_PT2_Q12_",
      ["C"],
    );

    // --- Q13: Vedic Students Examined ---
    // 2026 form: Q13_A=name, Q13_B=level, Q13_C=year(DatePicker), Q13_DOC=doc
    const q13Mapped = mapArrayWithCustomKeys(
      data.TEACHER_PT2_Q11,
      {
        A: "awardName",
        B: "Presenter",
        C: "year",
        DOC: "TEACHER_PT2_Q11_DOC",
      },
      "TEACHER_2026_PT2_Q13_",
      ["C"],
    );

    // --- Q14: Current Vedic Students ---
    const q14Mapped = mapArrayWithCustomKeys(
      data.TEACHER_PT2_Q12,
      {
        A: "title",
        B: "publisher",
        C: "isbn",
        D: "year",
        DOC: "TEACHER_PT2_Q12_DOC",
      },
      "TEACHER_2026_PT2_Q14_",
      ["D"],
    );

    const mappedData: { [key: string]: any } = {
      TEACHER_2026_PT2_Q1: data.TEACHER_PT2_Q1 || "",
      TEACHER_2026_PT2_Q2: data.TEACHER_PT2_Q2 || "",
      TEACHER_2026_PT2_Q3_A: data.TEACHER_PT2_Q3_A || "",
      TEACHER_2026_PT2_Q3_B: data.TEACHER_PT2_Q3_B || "",
      TEACHER_2026_PT2_Q3_C: data.TEACHER_PT2_Q3_C || "",
      TEACHER_2026_PT2_Q6: [
        {
          TEACHER_2026_PT2_Q6_A: "",
          TEACHER_2026_PT2_Q6_B: null,
          TEACHER_2026_PT2_Q6_C: null,
          TEACHER_2026_PT2_Q6_D: "",
        },
      ],
      TEACHER_2026_PT2_Q7: q7Mapped,
      TEACHER_2026_PT2_Q9: q9Mapped,

      TEACHER_2026_PT2_Q10: q10Mapped,
      TEACHER_2026_PT2_Q11: q11Mapped,
      TEACHER_2026_PT2_Q12: q12Mapped,
      TEACHER_2026_PT2_Q13: q13Mapped,
      TEACHER_2026_PT2_Q14: q14Mapped,
      TEACHER_2026_PT2_Q16: data.TEACHER_PT2_Q17 || "",
      TEACHER_2026_PT2_Q17_YEARS: data.TEACHER_PT2_Q18 || "",
      TEACHER_2026_PT2_Q19: data.TEACHER_PT2_Q20 || "",
      TEACHER_2026_PT2_Q20: data.TEACHER_PT2_Q21 || "",
      TEACHER_2026_PT2_Q21: data.TEACHER_PT2_Q22
        ? Utils.parseToDayjs(data.TEACHER_PT2_Q22)
        : null,
    };

    return Object.fromEntries(
      Object.entries(mappedData).filter(
        ([_, value]) => value !== null && value !== undefined && value !== "",
      ),
    );
  };

  const getPreviousFormData = async () => {
    setIsLoading(true);
    try {
      const payload: any = {
        data: {
          part: eSTAGE.STAGE2,
          year: CURRENT_YEAR - 1,
          userFormType: eUSER_FORM_TYPE.TEACHER,
          userId: userId,
          formFor: formFor,
        },
      };

      const response = await post(GetFormData, payload);
      const { dataResponse, data } = response;
      const { returnCode } = dataResponse;

      if (returnCode == eResultCode.SUCCESS) {
        const formData = Object.values(data?.teacherPt2Forms || {}).every(
          (value) => value === null,
        )
          ? {}
          : data?.teacherPt2Forms || {};

        const jsonStringFields = [
          "TEACHER_PT2_Q5",
          "TEACHER_PT2_Q6",
          "TEACHER_PT2_Q7",
          "TEACHER_PT2_Q8",
          "TEACHER_PT2_Q9",
          "TEACHER_PT2_Q10",
          "TEACHER_PT2_Q11",
          "TEACHER_PT2_Q12",
          "TEACHER_PT2_Q13",
          "TEACHER_PT2_Q14",
          "TEACHER_PT2_Q15",
          "TEACHER_PT2_Q16",
        ];

        const fileFields = [
          "TEACHER_PT2_Q4_E",
          "TEACHER_PT2_Q5_G",
          "TEACHER_PT2_Q11_DOC",
          "TEACHER_PT2_Q12_DOC",
          "TEACHER_PT2_Q13_DOC",
          "TEACHER_PT2_Q14_DOC",
          "TEACHER_PT2_Q19",
        ];

        const formattedData = Object.keys(formData).reduce(
          (acc: { [key: string]: any }, key) => {
            if (jsonStringFields.includes(key)) {
              try {
                const parsedArr = formData[key]
                  ? JSON.parse(formData[key])
                  : [{}];
                acc[key] = parsedArr;
              } catch (error) {
                acc[key] = [{}];
              }
            } else if (fileFields.includes(key)) {
              acc[key] = "";
            } else {
              acc[key] = formData[key];
            }
            return acc;
          },
          {},
        );

        // Handled completely by getMappedData safely now

        const mappedData = getMappedData(formattedData);

        // Q4 - Traditional Exams Passed
        // if (Array.isArray(mappedData.TEACHER_2026_PT2_Q4)) {
        //   mappedData.TEACHER_2026_PT2_Q4 = mappedData.TEACHER_2026_PT2_Q4.map(
        //     (row: any) => ({
        //       ...row,
        //       TEACHER_2026_PT2_Q4_C: undefined, // परीक्षा श्रेणी (Select)
        //       TEACHER_2026_PT2_Q4_D: undefined, // परीक्षण संस्थान (Select)
        //       TEACHER_2026_PT2_Q4_D_OTHER: undefined, // Other Institute Name
        //       TEACHER_2026_PT2_Q4_E: undefined, // Uploaded Certificate
        //     }),
        //   );
        // }

        // Q5 - Subjects Studied
        // if (Array.isArray(mappedData.TEACHER_2026_PT2_Q5)) {
        //   mappedData.TEACHER_2026_PT2_Q5 = mappedData.TEACHER_2026_PT2_Q5.map(
        //     (row: any) => ({
        //       ...row,
        //       TEACHER_2026_PT2_Q5_A: undefined, // विषय चुनें (Select)
        //       TEACHER_2026_PT2_Q5_A_OTHER: undefined, // Other Subject Name
        //       TEACHER_2026_PT2_Q5_E: undefined, // परीक्षा बोर्ड (Select)
        //       TEACHER_2026_PT2_Q5_E_OTHER: undefined, // Other Institute Name
        //       TEACHER_2026_PT2_Q5_F: undefined, // उत्तीर्ण श्रेणी (Select)
        //       TEACHER_2026_PT2_Q5_DOC: undefined, // Uploaded Certificate
        //     }),
        //   );
        // }

        if (mappedData && Object.keys(mappedData).length > 0) {
          form.setFieldsValue(sanitizeFormData(mappedData));
          setTimeout(() => {
            const uploadedDocuments = getUploadedDocuments();
            form.setFieldsValue({
              TEACHER_2026_PT2_Q15: uploadedDocuments,
            });
          }, 100);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const autoFillPartOneData = async () => {
    setIsLoading(true);
    try {
      const payload: any = {
        data: {
          id: formId,
          part: eSTAGE.STAGE1,
          isPtOne: true,
          year: CURRENT_YEAR,
          userFormType: eUSER_FORM_TYPE.TEACHER,
          userId: userId,
          formFor: formFor,
        },
      };
      const response = await post(GetFormData, payload);
      const { dataResponse, data } = response;
      const { returnCode } = dataResponse;
      if (returnCode == eResultCode.SUCCESS) {
        if (data && Object.keys(data).length > 0) {
          const name = data.find(
            (item: any) => item.questionKey === "TEACHER_2026_PT1_Q1",
          )?.answer;
          const aadhaar = data.find(
            (item: any) => item.questionKey === "TEACHER_2026_PT1_Q7",
          )?.answer;
          form.setFieldsValue({
            TEACHER_2026_PT2_Q1: name,
            TEACHER_2026_PT2_Q2: aadhaar,
            TEACHER_2026_PT2_Q19: name,
            TEACHER_2026_PT2_Q16: name,
          });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (formId && formFor && user) {
      const fetchData = async () => {
        await getPreviousFormData();
        await getFormData();
        await autoFillPartOneData();
      };
      fetchData();
    }
  }, [formId, formFor, userId, user]);

  // Function to collect all uploaded document titles from Q4 and Q5
  const getUploadedDocuments = () => {
    const documents: { documentDetails: string; documentUrl?: string }[] = [];

    // Collect from Q4 (Traditional Exam Certificates)
    const q4Data = form.getFieldValue("TEACHER_2026_PT2_Q4") || [];
    q4Data.forEach((item: any) => {
      if (item?.TEACHER_2026_PT2_Q4_E) {
        const examName = item?.TEACHER_2026_PT2_Q4_A
          ? dictionary?.[item.TEACHER_2026_PT2_Q4_A] ||
            item.TEACHER_2026_PT2_Q4_A
          : "Traditional Exam";
        documents.push({
          documentDetails: `${examName} Certificate`,
          documentUrl: item.TEACHER_2026_PT2_Q4_E,
        });
      }
    });

    // Collect from Q5 (Subject Certificates)
    const q5Data = form.getFieldValue("TEACHER_2026_PT2_Q5") || [];
    q5Data.forEach((item: any) => {
      if (item?.TEACHER_2026_PT2_Q5_DOC) {
        const subjectName = item?.TEACHER_2026_PT2_Q5_A
          ? Text({
              tid: `SUBJECT_OPTIONS_${item.TEACHER_2026_PT2_Q5_A.toUpperCase()}`,
              def: item.TEACHER_2026_PT2_Q5_A,
            })
          : "";
        const bookName = item?.TEACHER_2026_PT2_Q5_B || "";
        const title = [subjectName, bookName].filter(Boolean).join(" - ");

        documents.push({
          documentDetails: title
            ? `${title} Certificate`
            : "Subject Certificate",
          documentUrl: item.TEACHER_2026_PT2_Q5_DOC,
        });
      }
    });

    // Collect from Q11 (Assemblies)
    const q11Data = form.getFieldValue("TEACHER_2026_PT2_Q11") || [];
    q11Data.forEach((item: any) => {
      if (item?.TEACHER_2026_PT2_Q11_DOC) {
        const title = item?.TEACHER_2026_PT2_Q11_A || "Assembly";
        documents.push({
          documentDetails: `${title} Certificate`,
          documentUrl: item.TEACHER_2026_PT2_Q11_DOC,
        });
      }
    });

    // Collect from Q12 (Yagnas)
    const q12Data = form.getFieldValue("TEACHER_2026_PT2_Q12") || [];
    q12Data.forEach((item: any) => {
      if (item?.TEACHER_2026_PT2_Q12_DOC) {
        const title = item?.TEACHER_2026_PT2_Q12_A || "Yagna";
        documents.push({
          documentDetails: `${title} Certificate`,
          documentUrl: item.TEACHER_2026_PT2_Q12_DOC,
        });
      }
    });

    // Collect from Q13 (Awards)
    const q13Data = form.getFieldValue("TEACHER_2026_PT2_Q13") || [];
    q13Data.forEach((item: any) => {
      if (item?.TEACHER_2026_PT2_Q13_DOC) {
        const title = item?.TEACHER_2026_PT2_Q13_A || "Award";
        documents.push({
          documentDetails: `${title} Certificate`,
          documentUrl: item.TEACHER_2026_PT2_Q13_DOC,
        });
      }
    });

    // Collect from Q14 (Publications)
    const q14Data = form.getFieldValue("TEACHER_2026_PT2_Q14") || [];
    q14Data.forEach((item: any) => {
      if (item?.TEACHER_2026_PT2_Q14_DOC) {
        const title = item?.TEACHER_2026_PT2_Q14_A || "Publication";
        documents.push({
          documentDetails: `${title} Evidence`,
          documentUrl: item.TEACHER_2026_PT2_Q14_DOC,
        });
      }
    });

    return documents;
  };

  // Auto-update Q15 document list whenever documents are uploaded in Q4, Q5, or Q11-Q14
  const q4WatchData = Form.useWatch("TEACHER_2026_PT2_Q4", form);
  const q5WatchData = Form.useWatch("TEACHER_2026_PT2_Q5", form);
  const q11WatchData = Form.useWatch("TEACHER_2026_PT2_Q11", form);
  const q12WatchData = Form.useWatch("TEACHER_2026_PT2_Q12", form);
  const q13WatchData = Form.useWatch("TEACHER_2026_PT2_Q13", form);
  const q14WatchData = Form.useWatch("TEACHER_2026_PT2_Q14", form);

  const { handleFormSubmit, onFormValuesChange, debouncedSave } =
    useFormAutoSave({
      formId: formId || "",
      form,
      stage: eSTAGE.STAGE2,
      handleFieldChange,
    });

  useEffect(() => {
    const uploadedDocuments = getUploadedDocuments();
    form.setFieldsValue({
      TEACHER_2026_PT2_Q15: uploadedDocuments,
    });
    debouncedSave("TEACHER_2026_PT2_Q15", JSON.stringify(uploadedDocuments));
  }, [
    q4WatchData,
    q5WatchData,
    q11WatchData,
    q12WatchData,
    q13WatchData,
    q14WatchData,
  ]);

  useEffect(() => {
    const currentValue = form.getFieldValue("TEACHER_2026_PT2_Q21");

    if (!currentValue) {
      const defaultDate = dayjs(); // or your custom default date

      form.setFieldValue("TEACHER_2026_PT2_Q21", defaultDate);

      debouncedSave("TEACHER_2026_PT2_Q21", defaultDate);
    }
  }, [formId]);

  const handleLocalFormChange = (changedValues: any, allValues: any) => {
    onFormValuesChange(changedValues, allValues);

    // Q1 Side Effect: Copy Name to Q27, Q28
    if ("TEACHER_2026_PT2_Q1" in changedValues) {
      const value = changedValues.TEACHER_2026_PT2_Q1;
      if (value) {
        form.setFieldValue("TEACHER_2026_PT2_Q16", value);
        debouncedSave("TEACHER_2026_PT2_Q16", value);
        form.setFieldValue("TEACHER_2026_PT2_Q19", value);
        debouncedSave("TEACHER_2026_PT2_Q19", value);
      }
    }
  };

  const handleNextClick = async () => {
    try {
      await form.validateFields();
      setIsReviewMode(true);

      // When all fields valid → go to top
      document.getElementById("teacherFormPart2")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } catch (errorInfo: any) {
      console.log("Validation Failed:", errorInfo);

      if (errorInfo?.errorFields?.length > 0) {
        const firstError = errorInfo.errorFields[0];
        const fieldName = firstError.name;

        setTimeout(() => {
          // Antd scroll
          form.scrollToField(fieldName, {
            behavior: "smooth",
            block: "center",
          });

          // Focus input automatically
          const input = document.querySelector(
            `[id="${fieldName.join("_")}"]`,
          ) as HTMLElement;

          input?.focus();
        }, 200);
      }
    }
  };

  const handleBackClick = () => setIsReviewMode(false);

  // Q15 -----------------------------------------Q15-TABLE-COLUMNS-AND-DATA-SOURCE-----------------------------------------

  const TABLE_COLUMNS_TEACHER_2026_PT2_Q15: TableProps<any>["columns"] = [
    {
      title: Text({ tid: "SERIAL_NUMBER", def: "SERIAL_NUMBER" }),
      dataIndex: "serialNumber",
      key: "serialNumber",
      width: "100px",
      render: (_, record, index) => getNumberInLanguage(index + 1),
    },
    {
      title: Text({ tid: "DOCUMENT_DETAILS", def: "Document Details" }),
      dataIndex: "documentDetails",
      key: "documentDetails",
    },
    {
      title: Text({ tid: "PREVIEW", def: "Preview" }),
      dataIndex: "documentUrl",
      key: "documentUrl",
      render: (_, record: any) => {
        const documentUrl = record.documentUrl;
        if (!documentUrl)
          return <span style={{ color: "#ccc" }}>No document</span>;

        const fullUrl = `${process.env.REACT_APP_PUBLIC_API_MEDIA_BASEURL}${documentUrl}`;
        const isPdf = documentUrl.toLowerCase().endsWith(".pdf");

        if (isPdf) {
          return (
            <a
              href={fullUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="primary-link"
            >
              <FilePdfOutlined style={{ fontSize: "24px", color: "#ff4d4f" }} />
            </a>
          );
        }

        return <Image width={50} src={fullUrl} alt="Preview" />;
      },
    },
  ];

  const inputRequiredBasicRule: Rule[] = [
    {
      required: true,
      message: REQUIRED,
    },
  ];

  const handleDateChange = (
    form: any,
    fieldName: string,
    index: number,
    dateString: string,
    handleFieldChange: (field: string, value: any) => void,
  ) => {
    setTimeout(() => {
      const currentData = form.getFieldValue(fieldName) || [];
      handleFieldChange(fieldName, buildArrayPayload(currentData));
    }, 0);
  };

  const handleGeneratePDF = (id: string, fileName: string) => {
    setApplyPdfStyles(true);
    setTimeout(() => {
      Utils.generatePDF(id, fileName);
      setApplyPdfStyles(false);
    }, 0);
  };

  const teacherPt2Q7RowCount = 5;

  const DATA_SOURCE_TEACHER_PT2_Q7: any[] = Array.from(
    { length: teacherPt2Q7RowCount },
    (_, index) => ({
      TEACHER_2026_PT2_Q7_A: `${getNumberInLanguage(Number(new Date().getFullYear() - index))}`,
      TEACHER_2026_PT2_Q7_B: 0,
      TEACHER_2026_PT2_Q7_C: 0,
      TEACHER_2026_PT2_Q7_D: 0,
      TEACHER_2026_PT2_Q7_E: 0,
      TEACHER_2026_PT2_Q7_F: 0,
    }),
  );

  useEffect(() => {
    form.setFieldsValue({
      TEACHER_2026_PT2_Q7: DATA_SOURCE_TEACHER_PT2_Q7,
    });
  }, [form]);

  return (
    <div
      id="teacherFormPart2"
      className={`${styles.formWrapper} teacherPartTwo studentTeacherSchoolFormWrapper`}
    >
      <Form
        {...formItemLayout}
        form={form}
        name="register"
        onFinish={handleFormSubmit}
        onValuesChange={handleLocalFormChange}
        initialValues={{}}
        scrollToFirstError
        layout="vertical"
      >
        <Row>
          <Flex
            gap="middle"
            align="start"
            justify="space-between"
            style={{ width: "100%" }}
          >
            <Col>
              <Typography.Title level={3}>
                {Text({
                  tid: "TEACHER_2026_PART_2_FORM_HEADING",
                  def: "TEACHER_PART_2_FORM_HEADING",
                })}
              </Typography.Title>
              {!disabled && (
                <Typography.Text type="danger">
                  {Text({
                    tid: "all_fields_are_mandatory",
                    def: "All fields are mandatory!",
                  })}
                </Typography.Text>
              )}
            </Col>
          </Flex>
        </Row>
        {isReviewMode && (
          <Row style={{ marginBottom: 10 }}>
            <Typography.Text type="warning">
              <CheckCircleOutlined style={{ marginRight: 6 }} />
              {Text({
                tid: "REVIEW_MODE_INSTRUCTION",
                def: "You are in review mode. You can only view the submitted data. To make changes, please switch to edit mode.",
              })}
            </Typography.Text>
          </Row>
        )}
        <Row gutter={[16, 16]}>
          {/* 1. Name */}
          <Col xs={24} lg={12} xl={8}>
            <Form.Item
              name="TEACHER_2026_PT2_Q1"
              label={Text({
                tid: "TEACHER_2026_PT2_Q1",
                def: "पूरा नाम :*",
              })}
            >
              <Input disabled={true} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          {/* 2. Aadhaar Card No. */}
          <Col xs={24} lg={12} xl={8}>
            <Form.Item
              name="TEACHER_2026_PT2_Q2"
              label={Text({
                tid: "TEACHER_2026_PT2_Q2",
                def: "आधार कार्ड :*",
              })}
              rules={[
                {
                  required: true,
                  message: Text({ tid: "REQUIRED", def: "Required" }),
                },
                {
                  pattern: /^[2-9]{1}[0-9]{11}$/, // Aadhaar must be 12 digits and should not start with 0 or 1
                  message: Text({
                    tid: "aadhaar_card_validation",
                    def: "Aadhaar must be 12 digits and should not start with 0 or 1",
                  }),
                },
              ]}
            >
              <Input
                disabled={true}
                maxLength={12}
                onKeyDown={allowOnlyNumericInput_onKeyDown}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* 3. Kumaraadhyapak Details */}
        <Row>
          <Col xs={24} className="onClickRenderFields">
            <Typography.Text>
              {Text({
                tid: "TEACHER_2026_PT2_Q3",
                def: "यदि आप कुमाराध्यापक के रूप में नियुक्त है, तो पिछले ५ वर्षो की छात्र स्थिति बतायें :",
              })}
            </Typography.Text>
            <Space align="baseline" className="QuestionsSubSection">
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={8} xl={8}>
                  <Form.Item
                    name="TEACHER_2026_PT2_Q3_A"
                    label={Text({
                      tid: "TEACHER_2026_PT2_Q3_A",
                      def: "न्यूनतम",
                    })}
                  >
                    <Input
                      disabled={disabled || isReviewMode}
                      onKeyDown={allowOnlyNumericInput_onKeyDown}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={8} xl={8}>
                  <Form.Item
                    name="TEACHER_2026_PT2_Q3_B"
                    label={Text({ tid: "TEACHER_2026_PT2_Q3_B", def: "औसत" })}
                  >
                    <Input
                      disabled={disabled || isReviewMode}
                      onKeyDown={allowOnlyNumericInput_onKeyDown}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={8} xl={8}>
                  <Form.Item
                    name="TEACHER_2026_PT2_Q3_C"
                    label={Text({
                      tid: "TEACHER_2026_PT2_Q3_C",
                      def: "अधिकतम",
                    })}
                  >
                    <Input
                      disabled={disabled || isReviewMode}
                      onKeyDown={allowOnlyNumericInput_onKeyDown}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Space>
          </Col>
        </Row>

        {/* 4. Traditional Exams Passed */}
        <Row>
          <Col xs={24} className="onClickRenderFields">
            <Typography.Text>
              {Text({
                tid: "TEACHER_2026_PT2_Q4",
                def: "आपके द्वारा वेदविद्या सम्बन्धी उत्तीर्ण सभी परम्परागत वेदपरीक्षाओं का विवरण कालक्रमानुसार दीजिए :",
              })}
            </Typography.Text>

            <Form.List name="TEACHER_2026_PT2_Q4">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => {
                    return (
                      <Space
                        key={key}
                        align="baseline"
                        className="QuestionsSubSection"
                      >
                        <Row>
                          <Col xs={24} lg={12} xl={8}>
                            <Form.Item
                              className="w-full"
                              {...restField}
                              name={[name, "TEACHER_2026_PT2_Q4_A"]}
                              label={Text({
                                tid: "TEACHER_2026_PT2_Q4_A",
                                def: "परीक्षा नाम :*",
                              })}
                              rules={[{ required: true, message: REQUIRED }]}
                            >
                              <Input disabled={disabled || isReviewMode} />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row>
                          <Col xs={24} lg={12} xl={8}>
                            <Form.Item
                              className="w-full"
                              {...restField}
                              name={[name, "TEACHER_2026_PT2_Q4_B"]}
                              label={Text({
                                tid: "TEACHER_2026_PT2_Q4_B",
                                def: "परीक्षा माह व वर्ष :*",
                              })}
                              rules={[{ required: true, message: REQUIRED }]}
                            >
                              <DatePicker
                                disabled={disabled || isReviewMode}
                                picker="month"
                                format="MM-YYYY"
                                disabledDate={(current) =>
                                  current && current > dayjs().endOf("month")
                                }
                                placeholder={SELECT_DATE}
                                style={{ width: "100%" }}
                                onChange={(date, dateString) =>
                                  handleDateChange(
                                    form,
                                    "TEACHER_2026_PT2_Q4",
                                    index,
                                    Array.isArray(dateString)
                                      ? dateString[0]
                                      : dateString,
                                    handleFieldChange,
                                  )
                                }
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row>
                          <Col sm={24} lg={12} xl={8}>
                            <Form.Item
                              name={[name, "TEACHER_2026_PT2_Q4_C"]}
                              label={Text({
                                tid: "TEACHER_2026_PT2_Q4_C",
                                def: "परीक्षा श्रेणी :*",
                              })}
                              rules={[{ required: true, message: REQUIRED }]}
                            >
                              <Select
                                disabled={disabled || isReviewMode}
                                placeholder={DROPDOWN_PLACEHOLDER}
                                onChange={(val) => {
                                  if (
                                    val === "EXAM_PENDING" ||
                                    val === "EXAM_NOT_TAKEN"
                                  ) {
                                    form.setFieldValue(
                                      [
                                        "TEACHER_2026_PT2_Q4",
                                        name,
                                        "TEACHER_2026_PT2_Q4_E",
                                      ],
                                      "",
                                    );
                                    handleFieldChange(
                                      "TEACHER_2026_PT2_Q4",
                                      buildArrayPayload(
                                        form.getFieldValue(
                                          "TEACHER_2026_PT2_Q4",
                                        ),
                                      ),
                                    );
                                  }
                                }}
                              >
                                <Select.Option value="FIRST">
                                  {FIRST}
                                </Select.Option>
                                <Select.Option value="SECOND">
                                  {SECOND}
                                </Select.Option>
                                <Select.Option value="THIRD">
                                  {THIRD}
                                </Select.Option>
                                <Select.Option value="EXAM_PENDING">
                                  {EXAM_PENDING}
                                </Select.Option>
                                <Select.Option value="EXAM_NOT_TAKEN">
                                  {EXAM_NOT_TAKEN}
                                </Select.Option>
                              </Select>
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row>
                          <Col xs={24} lg={12} xl={8}>
                            <Form.Item
                              className="w-full"
                              {...restField}
                              name={[name, "TEACHER_2026_PT2_Q4_D"]}
                              label={Text({
                                tid: "TEACHER_2026_PT2_Q4_D",
                                def: "परीक्षण संस्थान :*",
                              })}
                              rules={[{ required: true, message: REQUIRED }]}
                            >
                              <Select
                                disabled={disabled || isReviewMode}
                                placeholder={DROPDOWN_PLACEHOLDER}
                                onChange={(value) =>
                                  handleFieldChange(
                                    "TEACHER_2026_PT1_Q18_D",
                                    value,
                                  )
                                }
                              >
                                {EXAM_INSTITUTE_LIST.map((item) => (
                                  <Select.Option
                                    key={item.value}
                                    value={item.value}
                                  >
                                    {Text({
                                      tid: item.value,
                                      def: item.label,
                                    })}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                        </Row>
                        {isOtherInstitute(index) && (
                          <Row>
                            <Col sm={24} lg={12} xl={8}>
                              <Form.Item
                                name="TEACHER_2026_PT2_Q4_D_OTHER"
                                label={Text({
                                  tid: "OTHER_INSTITUTE",
                                  def: "Other Institute Name",
                                })}
                                rules={inputRequiredBasicRule}
                              >
                                <Input
                                  disabled={disabled || isReviewMode}
                                  placeholder={Text({
                                    tid: "TEACHER_2026_PT2_Q4_D_OTHER_PLACEHOLDER",
                                    def: "Ex. Avadhoota Datta Peetham, Mysore",
                                  })}
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        )}
                        <Row>
                          <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) => {
                              const prevVal =
                                prevValues.TEACHER_2026_PT2_Q4?.[name]
                                  ?.TEACHER_2026_PT2_Q4_C;
                              const currVal =
                                currentValues.TEACHER_2026_PT2_Q4?.[name]
                                  ?.TEACHER_2026_PT2_Q4_C;
                              return prevVal !== currVal;
                            }}
                          >
                            {({ getFieldValue }) => {
                              const cat = getFieldValue([
                                "TEACHER_2026_PT2_Q4",
                                name,
                                "TEACHER_2026_PT2_Q4_C",
                              ]);
                              if (
                                cat === "EXAM_PENDING" ||
                                cat === "EXAM_NOT_TAKEN"
                              ) {
                                return null;
                              }
                              return (
                                <Col xs={24} lg={12} xl={8}>
                                  <div style={{ paddingLeft: "25px" }}>
                                    <DocumentUploadField
                                      formId={formId || ""}
                                      labelStyle={{ paddingLeft: "0px" }}
                                      userId={
                                        currentUserId
                                          ? currentUserId.toString()
                                          : ""
                                      }
                                      label={Text({
                                        tid: "TEACHER_2026_PT2_Q4_E",
                                        def: "प्रमाण पत्र संलग्न करें :*",
                                      })}
                                      fieldName={`TEACHER_2026_PT2_Q4_${index}_E`}
                                      existingDocuments={
                                        form.getFieldValue([
                                          "TEACHER_2026_PT2_Q4",
                                          name,
                                          "TEACHER_2026_PT2_Q4_E",
                                        ])
                                          ? [
                                              {
                                                docPath: form.getFieldValue([
                                                  "TEACHER_2026_PT2_Q4",
                                                  name,
                                                  "TEACHER_2026_PT2_Q4_E",
                                                ]),
                                                labelName: `TEACHER_2026_PT2_Q4_${index}_E`,
                                              },
                                            ]
                                          : []
                                      }
                                      disabled={disabled || isReviewMode}
                                      onUploadComplete={(docUrl) => {
                                        // Set form value for UI display only
                                        form.setFieldValue(
                                          [
                                            "TEACHER_2026_PT2_Q4",
                                            name,
                                            "TEACHER_2026_PT2_Q4_E",
                                          ],
                                          docUrl,
                                        );
                                        // Use patch to inject docUrl directly — avoids setFieldValue race condition
                                        handleFieldChange(
                                          "TEACHER_2026_PT2_Q4",
                                          buildArrayPayload(
                                            form.getFieldValue(
                                              "TEACHER_2026_PT2_Q4",
                                            ),
                                            [],
                                            {
                                              rowIndex: name,
                                              fieldKey: "TEACHER_2026_PT2_Q4_E",
                                              value: docUrl,
                                            },
                                          ),
                                        );
                                      }}
                                    />
                                  </div>
                                </Col>
                              );
                            }}
                          </Form.Item>
                        </Row>

                        <Row>
                          <Col xs={24}>
                            <Flex gap={30}>
                              {form.getFieldValue("TEACHER_2026_PT2_Q4")
                                .length > 1 && (
                                <Form.Item>
                                  <Button
                                    disabled={disabled || isReviewMode}
                                    variant="filled"
                                    className="actionButton"
                                    onClick={() => {
                                      remove(name);
                                      handleFieldChange(
                                        "TEACHER_2026_PT2_Q4",
                                        buildArrayPayload(
                                          form.getFieldValue(
                                            "TEACHER_2026_PT2_Q4",
                                          ),
                                        ),
                                      );
                                    }}
                                  >
                                    {REMOVE}
                                  </Button>
                                </Form.Item>
                              )}
                              {form.getFieldValue("TEACHER_2026_PT2_Q4")
                                .length -
                                1 ===
                                index && (
                                <Form.Item>
                                  <Button
                                    disabled={disabled || isReviewMode}
                                    variant="filled"
                                    className="actionButton"
                                    onClick={() => {
                                      add();
                                      handleFieldChange(
                                        "TEACHER_2026_PT2_Q4",
                                        buildArrayPayload(
                                          form.getFieldValue(
                                            "TEACHER_2026_PT2_Q4",
                                          ),
                                        ),
                                      );
                                    }}
                                    block
                                  >
                                    {ADD}
                                  </Button>
                                </Form.Item>
                              )}
                            </Flex>
                          </Col>
                        </Row>
                        {form.getFieldValue("TEACHER_2026_PT2_Q4").length - 1 >
                          index && <div className="divider"></div>}
                      </Space>
                    );
                  })}
                </>
              )}
            </Form.List>
          </Col>
        </Row>
        {/* 5. Subjects Studied */}
        <Row>
          <Col xs={24} className="onClickRenderFields">
            <Typography.Text>
              {Text({
                tid: "TEACHER_2026_PT2_Q5",
                def: "अधीत वेद के निम्न विषयों में आप द्वारा अर्जित सभी शिक्षाओं का विवरण दीजिये :",
              })}
            </Typography.Text>
            <Form.List name="TEACHER_2026_PT2_Q5">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <Space
                      key={key}
                      align="baseline"
                      className="QuestionsSubSection"
                    >
                      <Row>
                        <Col xs={24} lg={12} xl={8}>
                          <Form.Item
                            className="w-full"
                            {...restField}
                            name={[name, "TEACHER_2026_PT2_Q5_A"]}
                            label={Text({
                              tid: "TEACHER_2026_PT2_Q5_A",
                              def: "विषय चुनें",
                            })}
                            rules={[{ required: true, message: REQUIRED }]}
                          >
                            <Select
                              disabled={disabled || isReviewMode}
                              placeholder={DROPDOWN_PLACEHOLDER}
                            >
                              <Select.Option value="Brahmana">
                                {Text({
                                  tid: "SUBJECT_OPTIONS_BRAHMANA",
                                  def: "Brahmana",
                                })}
                              </Select.Option>
                              <Select.Option value="Aranyaka">
                                {Text({
                                  tid: "SUBJECT_OPTIONS_ARANYAKA",
                                  def: "Aranyaka",
                                })}
                              </Select.Option>
                              <Select.Option value="Upanishad">
                                {Text({
                                  tid: "SUBJECT_OPTIONS_UPANISHAD",
                                  def: "Upanishad",
                                })}
                              </Select.Option>
                              <Select.Option value="Shadanga">
                                {Text({
                                  tid: "SUBJECT_OPTIONS_SHADANGA",
                                  def: "Shadanga",
                                })}
                              </Select.Option>
                              <Select.Option value="Lakshana">
                                {Text({
                                  tid: "SUBJECT_OPTIONS_LAKSHANA",
                                  def: "Lakshana",
                                })}
                              </Select.Option>
                              <Select.Option value="Bhashya">
                                {Text({
                                  tid: "SUBJECT_OPTIONS_BHASHYA",
                                  def: "Bhashya",
                                })}
                              </Select.Option>
                              <Select.Option value="SUBJECT_OTHER">
                                {Text({
                                  tid: "SUBJECT_OPTIONS_OTHER",
                                  def: "Other",
                                })}
                              </Select.Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                      {isOtherSubject(index) && (
                        <Row>
                          <Col sm={24} lg={12} xl={8}>
                            <Form.Item
                              name="TEACHER_2026_PT2_Q5_A_OTHER"
                              label={Text({
                                tid: "OTHER_INSTITUTE",
                                def: "Other Institute Name",
                              })}
                              rules={inputRequiredBasicRule}
                            >
                              <Input
                                disabled={disabled || isReviewMode}
                                placeholder={Text({
                                  tid: "TEACHER_2026_PT2_Q5_A_OTHER_PLACEHOLDER",
                                  def: "",
                                })}
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      )}
                      <Row>
                        <Col xs={24} lg={12} xl={8}>
                          <Form.Item
                            className="w-full"
                            {...restField}
                            name={[name, "TEACHER_2026_PT2_Q5_B"]}
                            label={Text({
                              tid: "TEACHER_2026_PT2_Q5_B",
                              def: "ग्रन्थ नाम",
                            })}
                            rules={[{ required: true, message: REQUIRED }]}
                          >
                            <Input disabled={disabled || isReviewMode} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={24} lg={12} xl={8}>
                          <Form.Item
                            className="w-full"
                            {...restField}
                            name={[name, "TEACHER_2026_PT2_Q5_C"]}
                            label={Text({
                              tid: "TEACHER_2026_PT2_Q5_C",
                              def: "कितना अध्ययन किया है?",
                            })}
                            rules={[{ required: true, message: REQUIRED }]}
                          >
                            <Input disabled={disabled || isReviewMode} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={24} lg={12} xl={8}>
                          <Form.Item
                            className="w-full"
                            {...restField}
                            name={[name, "TEACHER_2026_PT2_Q5_D"]}
                            label={Text({
                              tid: "TEACHER_2026_PT2_Q5_D",
                              def: "परीक्षा माह व वर्ष",
                            })}
                            rules={[{ required: true, message: REQUIRED }]}
                          >
                            <DatePicker
                              disabled={disabled || isReviewMode}
                              picker="month"
                              format="MM-YYYY"
                              disabledDate={(current) =>
                                current && current > dayjs().endOf("month")
                              }
                              placeholder={SELECT_DATE}
                              style={{ width: "100%" }}
                              onChange={(date, dateString) =>
                                handleDateChange(
                                  form,
                                  "TEACHER_2026_PT2_Q5",
                                  index,
                                  Array.isArray(dateString)
                                    ? dateString[0]
                                    : dateString,
                                  handleFieldChange,
                                )
                              }
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={24} lg={12} xl={8}>
                          <Form.Item
                            className="w-full"
                            {...restField}
                            name={[name, "TEACHER_2026_PT2_Q5_E"]}
                            label={Text({
                              tid: "TEACHER_2026_PT2_Q5_E",
                              def: "परीक्षा बोर्ड",
                            })}
                            rules={[{ required: true, message: REQUIRED }]}
                          >
                            <Select
                              disabled={disabled || isReviewMode}
                              placeholder={DROPDOWN_PLACEHOLDER}
                            >
                              {EXAM_INSTITUTE_LIST.map((item) => (
                                <Select.Option
                                  key={item.value}
                                  value={item.value}
                                >
                                  {dictionary?.[item.value] || item.label}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => {
                          const prevVal =
                            prevValues.TEACHER_2026_PT2_Q5?.[name]
                              ?.TEACHER_2026_PT2_Q5_E;
                          const currVal =
                            currentValues.TEACHER_2026_PT2_Q5?.[name]
                              ?.TEACHER_2026_PT2_Q5_E;
                          return prevVal !== currVal;
                        }}
                      >
                        {({ getFieldValue }) =>
                          getFieldValue([
                            "TEACHER_2026_PT2_Q5",
                            name,
                            "TEACHER_2026_PT2_Q5_E",
                          ]) === "INSTITUTE_OTHER" ? (
                            <Row>
                              <Col xs={24} lg={12} xl={8}>
                                <Form.Item
                                  {...restField}
                                  name={[name, "TEACHER_2026_PT2_Q5_E_OTHER"]}
                                  label={Text({
                                    tid: "OTHER_INSTITUTE",
                                    def: "Other Institute Name",
                                  })}
                                  rules={inputRequiredBasicRule}
                                >
                                  <Input
                                    disabled={disabled || isReviewMode}
                                    placeholder={Text({
                                      tid: "TEACHER_2026_PT2_Q5_E_OTHER_PLACEHOLDER",
                                      def: "Ex. Sanatan Vaidik Shikshan Sansthan",
                                    })}
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          ) : null
                        }
                      </Form.Item>
                      <Row>
                        <Col sm={24} lg={12} xl={8}>
                          <Form.Item
                            name={[name, "TEACHER_2026_PT2_Q5_F"]}
                            label={Text({
                              tid: "TEACHER_2026_PT2_Q5_F",
                              def: "उत्तीर्ण श्रेणी",
                            })}
                            rules={[{ required: true, message: REQUIRED }]}
                          >
                            <Select
                              disabled={disabled || isReviewMode}
                              placeholder={DROPDOWN_PLACEHOLDER}
                              onChange={(val) => {
                                if (
                                  val === "EXAM_PENDING" ||
                                  val === "EXAM_NOT_TAKEN"
                                ) {
                                  form.setFieldValue(
                                    [
                                      "TEACHER_2026_PT2_Q5",
                                      name,
                                      "TEACHER_2026_PT2_Q5_DOC",
                                    ],
                                    "",
                                  );
                                  handleFieldChange(
                                    "TEACHER_2026_PT2_Q5",
                                    buildArrayPayload(
                                      form.getFieldValue("TEACHER_2026_PT2_Q5"),
                                    ),
                                  );
                                }
                              }}
                            >
                              <Select.Option value="FIRST">
                                {FIRST}
                              </Select.Option>
                              <Select.Option value="SECOND">
                                {SECOND}
                              </Select.Option>
                              <Select.Option value="THIRD">
                                {THIRD}
                              </Select.Option>
                              <Select.Option value="EXAM_PENDING">
                                {EXAM_PENDING}
                              </Select.Option>
                              <Select.Option value="EXAM_NOT_TAKEN">
                                {EXAM_NOT_TAKEN}
                              </Select.Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row>
                        <Form.Item
                          noStyle
                          shouldUpdate={(prevValues, currentValues) => {
                            const prevVal =
                              prevValues.TEACHER_2026_PT2_Q5?.[name]
                                ?.TEACHER_2026_PT2_Q5_F;
                            const currVal =
                              currentValues.TEACHER_2026_PT2_Q5?.[name]
                                ?.TEACHER_2026_PT2_Q5_F;
                            return prevVal !== currVal;
                          }}
                        >
                          {({ getFieldValue }) => {
                            const cat = getFieldValue([
                              "TEACHER_2026_PT2_Q5",
                              name,
                              "TEACHER_2026_PT2_Q5_F",
                            ]);
                            if (
                              cat === "EXAM_PENDING" ||
                              cat === "EXAM_NOT_TAKEN"
                            ) {
                              return null;
                            }
                            return (
                              <Col xs={24} lg={12} xl={8}>
                                <div style={{ paddingLeft: "25px" }}>
                                  <DocumentUploadField
                                    formId={formId || ""}
                                    labelStyle={{ paddingLeft: "0px" }}
                                    userId={
                                      currentUserId
                                        ? currentUserId.toString()
                                        : ""
                                    }
                                    label={Text({
                                      tid: "TEACHER_2026_PT2_Q5_DOC",
                                      def: "प्रमाणपत्र संलग्न करें :",
                                    })}
                                    fieldName={`TEACHER_2026_PT2_Q5_${index}_DOC`}
                                    existingDocuments={
                                      form.getFieldValue([
                                        "TEACHER_2026_PT2_Q5",
                                        name,
                                        "TEACHER_2026_PT2_Q5_DOC",
                                      ])
                                        ? [
                                            {
                                              docPath: form.getFieldValue([
                                                "TEACHER_2026_PT2_Q5",
                                                name,
                                                "TEACHER_2026_PT2_Q5_DOC",
                                              ]),
                                              labelName: `TEACHER_2026_PT2_Q5_${index}_DOC`,
                                            },
                                          ]
                                        : []
                                    }
                                    disabled={disabled || isReviewMode}
                                    onUploadComplete={(docUrl) => {
                                      // Set form value for UI display only
                                      form.setFieldValue(
                                        [
                                          "TEACHER_2026_PT2_Q5",
                                          name,
                                          "TEACHER_2026_PT2_Q5_DOC",
                                        ],
                                        docUrl,
                                      );
                                      // Use patch to inject docUrl directly — avoids setFieldValue race condition
                                      handleFieldChange(
                                        "TEACHER_2026_PT2_Q5",
                                        buildArrayPayload(
                                          form.getFieldValue(
                                            "TEACHER_2026_PT2_Q5",
                                          ),
                                          [],
                                          {
                                            rowIndex: name,
                                            fieldKey: "TEACHER_2026_PT2_Q5_DOC",
                                            value: docUrl,
                                          },
                                        ),
                                      );
                                    }}
                                  />
                                </div>
                              </Col>
                            );
                          }}
                        </Form.Item>
                      </Row>

                      <Row>
                        <Col xs={24}>
                          <Flex gap={30}>
                            {form.getFieldValue("TEACHER_2026_PT2_Q5").length >
                              1 && (
                              <Form.Item>
                                <Button
                                  disabled={disabled || isReviewMode}
                                  variant="filled"
                                  className="actionButton"
                                  onClick={() => {
                                    remove(name);
                                    handleFieldChange(
                                      "TEACHER_2026_PT2_Q5",
                                      buildArrayPayload(
                                        form.getFieldValue(
                                          "TEACHER_2026_PT2_Q5",
                                        ),
                                      ),
                                    );
                                  }}
                                >
                                  {REMOVE}
                                </Button>
                              </Form.Item>
                            )}
                            {form.getFieldValue("TEACHER_2026_PT2_Q5").length -
                              1 ===
                              index && (
                              <Form.Item>
                                <Button
                                  disabled={disabled || isReviewMode}
                                  variant="filled"
                                  className="actionButton"
                                  onClick={() => {
                                    add();
                                    handleFieldChange(
                                      "TEACHER_2026_PT2_Q5",
                                      buildArrayPayload(
                                        form.getFieldValue(
                                          "TEACHER_2026_PT2_Q5",
                                        ),
                                      ),
                                    );
                                  }}
                                  block
                                >
                                  {ADD}
                                </Button>
                              </Form.Item>
                            )}
                          </Flex>
                        </Col>
                      </Row>
                      {form.getFieldValue("TEACHER_2026_PT2_Q5").length - 1 >
                        index && <div className="divider"></div>}
                    </Space>
                  ))}
                </>
              )}
            </Form.List>
          </Col>
        </Row>
        {/* 6. अधीत वेद ग्रन्थों का विवरण */}
        <Row>
          <Col xs={24} className="onClickRenderFields">
            <Typography.Text>
              {Text({
                tid: "TEACHER_2026_PT2_Q6",
                def: "६. आपने वेद एवं वेदग्रन्थों का अध्ययन किन-किन गुरुजनों के सान्निध्य में एवं किस-किस विद्यालय में किया हैं? विवरण दीजिए :",
              })}
            </Typography.Text>
            <Form.List name="TEACHER_2026_PT2_Q6">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <Space
                      key={key}
                      align="baseline"
                      className="QuestionsSubSection"
                    >
                      <Row>
                        <Col xs={24} lg={12} xl={8}>
                          <Form.Item
                            className="w-full"
                            {...restField}
                            name={[name, "TEACHER_2026_PT2_Q6_A"]}
                            label={Text({
                              tid: "TEACHER_2026_PT2_Q6_A",
                              def: "अधीत वेद / ग्रन्थ:*",
                            })}
                            rules={[{ required: true, message: REQUIRED }]}
                          >
                            <Input disabled={disabled || isReviewMode} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={24} lg={12} xl={8}>
                          <Form.Item
                            className="w-full"
                            {...restField}
                            name={[name, "TEACHER_2026_PT2_Q6_B"]}
                            label={Text({
                              tid: "TEACHER_2026_PT2_Q6_B",
                              def: "अध्ययन प्रारम्भ:*",
                            })}
                            rules={[{ required: true, message: REQUIRED }]}
                          >
                            <DatePicker
                              disabled={disabled || isReviewMode}
                              picker="month"
                              format="MM-YYYY"
                              disabledDate={(current) =>
                                current && current > dayjs().endOf("month")
                              }
                              placeholder={SELECT_DATE}
                              style={{ width: "100%" }}
                              onChange={(date, dateString) =>
                                handleDateChange(
                                  form,
                                  "TEACHER_2026_PT2_Q6",
                                  index,
                                  Array.isArray(dateString)
                                    ? dateString[0]
                                    : dateString,
                                  handleFieldChange,
                                )
                              }
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={24} lg={12} xl={8}>
                          <Form.Item
                            className="w-full"
                            {...restField}
                            name={[name, "TEACHER_2026_PT2_Q6_C"]}
                            label={Text({
                              tid: "TEACHER_2026_PT2_Q6_C",
                              def: "अध्ययन समाप्त:*",
                            })}
                            rules={[{ required: true, message: REQUIRED }]}
                          >
                            <DatePicker
                              disabled={disabled || isReviewMode}
                              picker="month"
                              format="MM-YYYY"
                              disabledDate={(current) =>
                                current && current > dayjs().endOf("month")
                              }
                              placeholder={SELECT_DATE}
                              style={{ width: "100%" }}
                              onChange={(date, dateString) =>
                                handleDateChange(
                                  form,
                                  "TEACHER_2026_PT2_Q6",
                                  index,
                                  Array.isArray(dateString)
                                    ? dateString[0]
                                    : dateString,
                                  handleFieldChange,
                                )
                              }
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={24} lg={12} xl={8}>
                          <Form.Item
                            className="w-full"
                            {...restField}
                            name={[name, "TEACHER_2026_PT2_Q6_D"]}
                            label={Text({
                              tid: "TEACHER_2026_PT2_Q6_D",
                              def: "गुरुजी का नाम:*",
                            })}
                            rules={[{ required: true, message: REQUIRED }]}
                          >
                            <Input disabled={disabled || isReviewMode} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={24} lg={12} xl={8}>
                          <Form.Item
                            className="w-full"
                            {...restField}
                            name={[name, "TEACHER_2026_PT2_Q6_E"]}
                            label={Text({
                              tid: "TEACHER_2026_PT2_Q6_E",
                              def: "गुरुजी से सम्बन्ध:*",
                            })}
                            rules={[{ required: true, message: REQUIRED }]}
                          >
                            <Input disabled={disabled || isReviewMode} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={24} lg={12} xl={8}>
                          <Form.Item
                            className="w-full"
                            {...restField}
                            name={[name, "TEACHER_2026_PT2_Q6_F"]}
                            label={Text({
                              tid: "TEACHER_2026_PT2_Q6_F",
                              def: "वेदविद्यालय का नाम:*",
                            })}
                            rules={[{ required: true, message: REQUIRED }]}
                          >
                            <Input disabled={disabled || isReviewMode} />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Row style={{ marginTop: "25px" }}>
                        <Col xs={24}>
                          <Flex gap={30}>
                            {form.getFieldValue("TEACHER_2026_PT2_Q6").length >
                              1 && (
                              <Form.Item>
                                <Button
                                  disabled={disabled || isReviewMode}
                                  variant="filled"
                                  className="actionButton"
                                  onClick={() => {
                                    remove(name);
                                    handleFieldChange(
                                      "TEACHER_2026_PT2_Q6",
                                      buildArrayPayload(
                                        form.getFieldValue(
                                          "TEACHER_2026_PT2_Q6",
                                        ),
                                      ),
                                    );
                                  }}
                                >
                                  {REMOVE}
                                </Button>
                              </Form.Item>
                            )}

                            {form.getFieldValue("TEACHER_2026_PT2_Q6").length -
                              1 ===
                              index && (
                              <Form.Item>
                                <Button
                                  disabled={disabled || isReviewMode}
                                  variant="filled"
                                  className="actionButton"
                                  onClick={() => {
                                    add();
                                    handleFieldChange(
                                      "TEACHER_2026_PT2_Q6",
                                      buildArrayPayload(
                                        form.getFieldValue(
                                          "TEACHER_2026_PT2_Q6",
                                        ),
                                      ),
                                    );
                                  }}
                                  block
                                >
                                  {ADD}
                                </Button>
                              </Form.Item>
                            )}
                          </Flex>
                        </Col>
                      </Row>
                      {form.getFieldValue("TEACHER_2026_PT2_Q6").length - 1 >
                        index && <div className="divider"></div>}
                    </Space>
                  ))}
                </>
              )}
            </Form.List>
          </Col>
        </Row>
        {/* 7. 5-Years Students Passing */}
        <Row className={styles.tableMargin}>
          <Col xs={24} className="onClickRenderFields">
            <Typography.Text>
              {Text({
                tid: "TEACHER_2026_PT2_Q7",
                def: "७. विगत पाँच वर्षों में आपके वेदविद्यार्थियों की संख्या जो निम्न परीक्षाओं मे उत्तीर्ण हुए :",
              })}
            </Typography.Text>
            {(() => {
              const onChangeNumber = (index: number) => {
                const row = form.getFieldValue(["TEACHER_2026_PT2_Q7", index]);
                const total =
                  (Number(row?.TEACHER_2026_PT2_Q7_B) || 0) +
                  (Number(row?.TEACHER_2026_PT2_Q7_C) || 0) +
                  (Number(row?.TEACHER_2026_PT2_Q7_D) || 0) +
                  (Number(row?.TEACHER_2026_PT2_Q7_E) || 0);

                form.setFieldValue(
                  ["TEACHER_2026_PT2_Q7", index, "TEACHER_2026_PT2_Q7_F"],
                  total.toString(),
                );
              };

              const TABLE_COLUMNS_TEACHER_PT2_Q7: TableProps<any>["columns"] = [
                {
                  title: Text({
                    tid: "TEACHER_2026_PT2_Q7_A",
                    def: "TEACHER_2026_PT2_Q7_A",
                  }),
                  dataIndex: "TEACHER_2026_PT2_Q7_A",
                  width: 100,
                  key: "TEACHER_2026_PT2_Q7_A",
                },
                {
                  title: Text({
                    tid: "TEAHCER_2026_PT2_Q7_B",
                    def: "मूलांत",
                  }),
                  dataIndex: "TEACHER_2026_PT2_Q7_B",
                  key: "TEACHER_2026_PT2_Q7_B",
                  render: (_, record, index) => (
                    <Form.Item
                      name={[
                        "TEACHER_2026_PT2_Q7",
                        index,
                        "TEACHER_2026_PT2_Q7_B",
                      ]}
                      style={{ margin: 0 }}
                    >
                      <Input
                        type="number"
                        disabled={disabled || isReviewMode}
                        onChange={() => onChangeNumber(index)}
                        min={0}
                      />
                    </Form.Item>
                  ),
                },
                {
                  title: Text({
                    tid: "TEACHER_2026_PT2_Q7_C",
                    def: "स्तर-१",
                  }),
                  dataIndex: "TEACHER_2026_PT2_Q7_C",
                  key: "TEACHER_2026_PT2_Q7_C",
                  render: (_, record, index) => (
                    <Form.Item
                      name={[
                        "TEACHER_2026_PT2_Q7",
                        index,
                        "TEACHER_2026_PT2_Q7_C",
                      ]}
                      style={{ margin: 0 }}
                    >
                      <Input
                        type="number"
                        disabled={disabled || isReviewMode}
                        onChange={() => onChangeNumber(index)}
                        min={0}
                      />
                    </Form.Item>
                  ),
                },
                {
                  title: Text({
                    tid: "TEACHER_2026_PT2_Q7_D",
                    def: "स्तर-२",
                  }),
                  dataIndex: "TEACHER_2026_PT2_Q7_D",
                  key: "TEACHER_2026_PT2_Q7_D",
                  render: (_, record, index) => (
                    <Form.Item
                      name={[
                        "TEACHER_2026_PT2_Q7",
                        index,
                        "TEACHER_2026_PT2_Q7_D",
                      ]}
                      style={{ margin: 0 }}
                    >
                      <Input
                        type="number"
                        disabled={disabled || isReviewMode}
                        onChange={() => onChangeNumber(index)}
                        min={0}
                      />
                    </Form.Item>
                  ),
                },
                {
                  title: Text({ tid: "TEACHER_2026_PT2_Q7_E", def: "अन्य" }),
                  dataIndex: "TEACHER_2026_PT2_Q7_E",
                  key: "TEACHER_2026_PT2_Q7_E",
                  render: (_, record, index) => (
                    <Form.Item
                      name={[
                        "TEACHER_2026_PT2_Q7",
                        index,
                        "TEACHER_2026_PT2_Q7_E",
                      ]}
                      style={{ margin: 0 }}
                    >
                      <Input
                        type="number"
                        disabled={disabled || isReviewMode}
                        onChange={() => onChangeNumber(index)}
                        min={0}
                      />
                    </Form.Item>
                  ),
                },
                {
                  title: Text({
                    tid: "TEACHER_2026_PT2_Q7_F",
                    def: "कुलयोग",
                  }),
                  dataIndex: "TEACHER_2026_PT2_Q7_F",
                  key: "TEACHER_2026_PT2_Q7_F",
                  render: (_, record, index) => (
                    <Form.Item
                      name={[
                        "TEACHER_2026_PT2_Q7",
                        index,
                        "TEACHER_2026_PT2_Q7_F",
                      ]}
                      style={{ margin: 0 }}
                    >
                      <Input
                        disabled
                        bordered={false}
                        style={{
                          textAlign: "center",
                          fontWeight: "bold",
                          color: "inherit",
                        }}
                      />
                    </Form.Item>
                  ),
                },
              ];

              return (
                <Form.Item name="TEACHER_2026_PT2_Q7">
                  <Table<any>
                    columns={TABLE_COLUMNS_TEACHER_PT2_Q7}
                    size="large"
                    scroll={{ x: "80vw" }}
                    dataSource={DATA_SOURCE_TEACHER_PT2_Q7}
                    pagination={false}
                    style={{ width: "100%", marginTop: "1rem" }}
                  />
                </Form.Item>
              );
            })()}
          </Col>
        </Row>

        {/* 8. Level 2+ Passed Lifetime */}
        <Row className={styles.tableMargin}>
          <Col xs={24} className="onClickRenderFields">
            <Typography.Text>
              {Text({
                tid: "TEACHER_2026_PT2_Q8",
                def: "८. आपके जीवनकाल में आपके द्वारा अध्यापित वेदविद्यार्थियों का विवरण दीजिए, जिन्होंने स्तर-२ अथवा इससे उच्च स्तर की परीक्षाएँ उत्तीर्ण की हो :",
              })}
            </Typography.Text>
            <Form.List name="TEACHER_2026_PT2_Q8">
              {(fields, { add, remove }) => {
                const columns: TableProps<any>["columns"] = [
                  {
                    title: Text({
                      tid: "TEACHER_2026_PT2_Q8_A",
                      def: "वेदविद्यार्थी नाम",
                    }),
                    dataIndex: "name",
                    key: "name_col",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q8_A"]}
                        rules={[{ required: true, message: REQUIRED }]}
                        style={{ margin: 0 }}
                      >
                        <Input disabled={disabled || isReviewMode} />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({
                      tid: "TEACHER_2026_PT2_Q8_B",
                      def: "वेदाध्ययन स्तर",
                    }),
                    dataIndex: "level",
                    key: "level_col",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q8_B"]}
                        rules={[{ required: true, message: REQUIRED }]}
                        style={{ margin: 0 }}
                      >
                        <Input disabled={disabled || isReviewMode} />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({
                      tid: "TEACHER_2026_PT2_Q8_C",
                      def: "परीक्षा संस्था का नाम",
                    }),
                    dataIndex: "institute",
                    key: "institute_col",
                    render: (_, field) => (
                      <Space direction="vertical" style={{ width: "100%" }}>
                        <Form.Item
                          {...field}
                          name={[field.name, "TEACHER_2026_PT2_Q8_C"]}
                          rules={[{ required: true, message: REQUIRED }]}
                          style={{ margin: 0 }}
                        >
                          <Select
                            disabled={disabled || isReviewMode}
                            placeholder={DROPDOWN_PLACEHOLDER}
                            style={{ minWidth: 150 }}
                          >
                            {EXAM_INSTITUTE_LIST.map((item) => (
                              <Select.Option
                                key={item.value}
                                value={item.value}
                              >
                                {dictionary?.[item.value] || item.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <Form.Item
                          noStyle
                          shouldUpdate={(prevValues, currentValues) => {
                            const prevVal =
                              prevValues.TEACHER_2026_PT2_Q8?.[field.name]
                                ?.TEACHER_2026_PT2_Q8_C;
                            const currVal =
                              currentValues.TEACHER_2026_PT2_Q8?.[field.name]
                                ?.TEACHER_2026_PT2_Q8_C;
                            return prevVal !== currVal;
                          }}
                        >
                          {({ getFieldValue }) =>
                            getFieldValue([
                              "TEACHER_2026_PT2_Q8",
                              field.name,
                              "TEACHER_2026_PT2_Q8_C",
                            ]) === "INSTITUTE_OTHER" ? (
                              <Form.Item
                                name={[
                                  field.name,
                                  "TEACHER_2026_PT2_Q8_C_OTHER",
                                ]}
                                rules={inputRequiredBasicRule}
                                style={{ margin: 0 }}
                              >
                                <Input
                                  disabled={disabled || isReviewMode}
                                  placeholder={Text({
                                    tid: "TEACHER_2026_PT2_Q8_C_OTHER_PLACEHOLDER",
                                    def: "Ex. Sanatan Vaidik Shikshan Sansthan",
                                  })}
                                />
                              </Form.Item>
                            ) : null
                          }
                        </Form.Item>
                      </Space>
                    ),
                  },
                  {
                    title: Text({
                      tid: "TEACHER_2026_PT2_Q8_D",
                      def: "परीक्षा वर्ष",
                    }),
                    dataIndex: "year",

                    key: "year_col",
                    render: (_, field, index) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q8_D"]}
                        rules={[{ required: true, message: REQUIRED }]}
                        style={{ margin: 0 }}
                      >
                        <DatePicker
                          disabled={disabled || isReviewMode}
                          picker="year"
                          format="YYYY"
                          disabledDate={(current) =>
                            current && current > dayjs().endOf("year")
                          }
                          placeholder={Text({
                            tid: "SELECT_YEAR",
                            def: "Select Year",
                          })}
                          style={{ width: "100%", minWidth: 120 }}
                          onChange={(date, dateString) =>
                            handleDateChange(
                              form,
                              "TEACHER_2026_PT2_Q8",
                              index,
                              Array.isArray(dateString)
                                ? dateString[0]
                                : dateString,
                              handleFieldChange,
                            )
                          }
                        />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({ tid: "ACTION", def: "Action" }),
                    key: "action",
                    width: 300,
                    render: (_, field, index) => (
                      <Space
                        style={{
                          display: "flex",
                          gap: "5px",
                          flexWrap: "wrap",
                        }}
                      >
                        {fields.length > 1 && (
                          <Button
                            disabled={disabled || isReviewMode}
                            variant="filled"
                            className="actionButton"
                            onClick={() => {
                              remove(field.name);
                              handleFieldChange(
                                "TEACHER_2026_PT2_Q8",
                                buildArrayPayload(
                                  form.getFieldValue("TEACHER_2026_PT2_Q8") ||
                                    [],
                                ),
                              );
                            }}
                          >
                            {REMOVE}
                          </Button>
                        )}
                        {index === fields.length - 1 && (
                          <Button
                            disabled={disabled || isReviewMode}
                            variant="filled"
                            className="actionButton"
                            onClick={() => {
                              add();
                              setTimeout(() => {
                                handleFieldChange(
                                  "TEACHER_2026_PT2_Q8",
                                  buildArrayPayload(
                                    form.getFieldValue("TEACHER_2026_PT2_Q8") ||
                                      [],
                                  ),
                                );
                              }, 0);
                            }}
                          >
                            {ADD}
                          </Button>
                        )}
                      </Space>
                    ),
                  },
                ];

                return (
                  <Table
                    dataSource={fields}
                    columns={columns}
                    pagination={false}
                    rowKey="key"
                    scroll={{ x: "max-content" }}
                  />
                );
              }}
            </Form.List>
          </Col>
        </Row>
        {/* 9. Current Students */}
        <Row className={styles.tableMargin}>
          <Col xs={24} className="onClickRenderFields">
            <Typography.Text>
              {Text({
                tid: "TEACHER_2026_PT2_Q9",
                def: "९. वर्तमान में आपसे वेदाध्ययन कर रहे वेदविद्यार्थियों का विवरण दीजिए :",
              })}
            </Typography.Text>
            <Form.List name="TEACHER_2026_PT2_Q9">
              {(fields, { add, remove }) => {
                const columns: TableProps<any>["columns"] = [
                  {
                    title: Text({
                      tid: "TEACHER_2026_PT2_Q9_A",
                      def: "वेदविद्यार्थी का नाम :",
                    }),
                    dataIndex: "name",
                    key: "name_col",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q9_A"]}
                        rules={[{ required: true, message: REQUIRED }]}
                        style={{ margin: 0 }}
                      >
                        <Input disabled={disabled || isReviewMode} />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({ tid: "TEACHER_2026_PT2_Q9_B", def: "आयु :" }),
                    dataIndex: "age",
                    key: "age_col",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q9_B"]}
                        rules={[{ required: true, message: REQUIRED }]}
                        style={{ margin: 0 }}
                      >
                        <Input
                          type="number"
                          min={0}
                          disabled={disabled || isReviewMode}
                        />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({
                      tid: "TEACHER_2026_PT2_Q9_C",
                      def: "शिक्षा प्रारंभ वर्ष :",
                    }),
                    dataIndex: "year",
                    key: "year_col",
                    render: (_, field, index) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q9_C"]}
                        rules={[{ required: true, message: REQUIRED }]}
                        style={{ margin: 0 }}
                      >
                        <DatePicker
                          disabled={disabled || isReviewMode}
                          picker="year"
                          format="YYYY"
                          disabledDate={(current) =>
                            current && current > dayjs().endOf("year")
                          }
                          placeholder={Text({
                            tid: "SELECT_YEAR",
                            def: "Select Year",
                          })}
                          style={{ width: "100%", minWidth: 120 }}
                          onChange={(date, dateString) =>
                            handleDateChange(
                              form,
                              "TEACHER_2026_PT2_Q9",
                              index,
                              Array.isArray(dateString)
                                ? dateString[0]
                                : dateString,
                              handleFieldChange,
                            )
                          }
                        />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({
                      tid: "TEACHER_2026_PT2_Q9_D",
                      def: "वर्तमान शिक्षा स्तर :",
                    }),
                    dataIndex: "level",
                    key: "level_col",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q9_D"]}
                        rules={[{ required: true, message: REQUIRED }]}
                        style={{ margin: 0 }}
                      >
                        <Input disabled={disabled || isReviewMode} />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({ tid: "ACTION", def: "Action" }),
                    key: "action",
                    width: 300,
                    render: (_, field, index) => (
                      <Space
                        style={{
                          display: "flex",
                          gap: "5px",
                          flexWrap: "wrap",
                        }}
                      >
                        {fields.length > 1 && (
                          <Button
                            disabled={disabled || isReviewMode}
                            variant="filled"
                            className="actionButton"
                            onClick={() => {
                              remove(field.name);
                              handleFieldChange(
                                "TEACHER_2026_PT2_Q9",
                                buildArrayPayload(
                                  form.getFieldValue("TEACHER_2026_PT2_Q9") ||
                                    [],
                                ),
                              );
                            }}
                          >
                            {REMOVE}
                          </Button>
                        )}
                        {index === fields.length - 1 && (
                          <Button
                            disabled={disabled || isReviewMode}
                            variant="filled"
                            className="actionButton"
                            onClick={() => {
                              add();
                              setTimeout(() => {
                                handleFieldChange(
                                  "TEACHER_2026_PT2_Q9",
                                  buildArrayPayload(
                                    form.getFieldValue("TEACHER_2026_PT2_Q9") ||
                                      [],
                                  ),
                                );
                              }, 0);
                            }}
                          >
                            {ADD}
                          </Button>
                        )}
                      </Space>
                    ),
                  },
                ];

                return (
                  <Table
                    dataSource={fields}
                    columns={columns}
                    pagination={false}
                    rowKey="key"
                    scroll={{ x: "max-content" }}
                  />
                );
              }}
            </Form.List>
          </Col>
        </Row>

        {/* 10. Alumni Teaching */}
        <Row className={styles.tableMargin}>
          <Col xs={24} className="onClickRenderFields">
            <Typography.Text>
              {Text({
                tid: "TEACHER_2026_PT2_Q10",
                def: "१०. आपके जीवनकाल में आप द्वारा अध्यापित ऐसे वेदविद्यार्थियों का विवरण दीजिए, जो वेदाध्ययनोपरान्त वेदाध्यापन कर रहे हैं :",
              })}
            </Typography.Text>
            <Form.List name="TEACHER_2026_PT2_Q10">
              {(fields, { add, remove }) => {
                const columns: TableProps<any>["columns"] = [
                  {
                    title: Text({
                      tid: "TEACHER_2026_PT2_Q10_A",
                      def: "वेदविद्यार्थी का नाम :",
                    }),
                    dataIndex: "name",
                    key: "name_col",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q10_A"]}
                        rules={[{ required: true, message: REQUIRED }]}
                        style={{ margin: 0 }}
                      >
                        <Input disabled={disabled || isReviewMode} />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({
                      tid: "TEACHER_2026_PT2_Q10_B",
                      def: "वेदाध्ययन समाप्ति वर्ष :",
                    }),
                    dataIndex: "year",
                    key: "year_col",
                    render: (_, field, index) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q10_B"]}
                        rules={[{ required: true, message: REQUIRED }]}
                        style={{ margin: 0 }}
                      >
                        <DatePicker
                          disabled={disabled || isReviewMode}
                          picker="year"
                          format="YYYY"
                          disabledDate={(current) =>
                            current && current > dayjs().endOf("year")
                          }
                          placeholder={Text({
                            tid: "SELECT_YEAR",
                            def: "Select Year",
                          })}
                          style={{ width: "100%", minWidth: 120 }}
                          onChange={(date, dateString) =>
                            handleDateChange(
                              form,
                              "TEACHER_2026_PT2_Q10",
                              index,
                              Array.isArray(dateString)
                                ? dateString[0]
                                : dateString,
                              handleFieldChange,
                            )
                          }
                        />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({
                      tid: "TEACHER_2026_PT2_Q10_C",
                      def: "वर्तमान वेदाध्यापन स्थल एवं संपर्क सूत्र :",
                    }),
                    dataIndex: "location",
                    key: "location_col",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q10_C"]}
                        rules={[{ required: true, message: REQUIRED }]}
                        style={{ margin: 0 }}
                      >
                        <Input disabled={disabled || isReviewMode} />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({ tid: "ACTION", def: "Action" }),
                    key: "action",
                    width: 300,
                    render: (_, field, index) => (
                      <Space
                        style={{
                          display: "flex",
                          gap: "5px",
                          flexWrap: "wrap",
                        }}
                      >
                        {fields.length > 1 && (
                          <Button
                            disabled={disabled || isReviewMode}
                            variant="filled"
                            className="actionButton"
                            onClick={() => {
                              remove(field.name);
                              handleFieldChange(
                                "TEACHER_2026_PT2_Q10",
                                buildArrayPayload(
                                  form.getFieldValue("TEACHER_2026_PT2_Q10") ||
                                    [],
                                ),
                              );
                            }}
                          >
                            {REMOVE}
                          </Button>
                        )}
                        {index === fields.length - 1 && (
                          <Button
                            disabled={disabled || isReviewMode}
                            variant="filled"
                            className="actionButton"
                            onClick={() => {
                              add();
                              setTimeout(() => {
                                handleFieldChange(
                                  "TEACHER_2026_PT2_Q10",
                                  buildArrayPayload(
                                    form.getFieldValue(
                                      "TEACHER_2026_PT2_Q10",
                                    ) || [],
                                  ),
                                );
                              }, 0);
                            }}
                          >
                            {ADD}
                          </Button>
                        )}
                      </Space>
                    ),
                  },
                ];

                return (
                  <Table
                    dataSource={fields}
                    columns={columns}
                    pagination={false}
                    rowKey="key"
                    scroll={{ x: "max-content" }}
                  />
                );
              }}
            </Form.List>
          </Col>
        </Row>

        {/* 11. Assemblies/Seminars */}
        <Row className={styles.tableMargin}>
          <Col xs={24} className="onClickRenderFields">
            <Typography.Text>
              {Text({
                tid: "TEACHER_2026_PT2_Q11",
                def: "११. वेदसभाओं, संगोष्ठीयों, कार्यशालाओं इत्यादि में आपकी उपस्थिति :",
              })}
            </Typography.Text>
            <Form.List name="TEACHER_2026_PT2_Q11">
              {(fields, { add, remove }) => {
                const columns: TableProps<any>["columns"] = [
                  {
                    title: Text({
                      tid: "TEACHER_2026_PT2_Q11_A",
                      def: "सभा / सम्मेलन",
                    }),
                    dataIndex: "assembly",
                    key: "assembly_col",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q11_A"]}
                        style={{ margin: 0 }}
                      >
                        <Input disabled={disabled || isReviewMode} />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({
                      tid: "TEACHER_2026_PT2_Q11_B",
                      def: "आयोजक",
                    }),
                    dataIndex: "organizer",
                    key: "organizer_col",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q11_B"]}
                        style={{ margin: 0 }}
                      >
                        <Input disabled={disabled || isReviewMode} />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({
                      tid: "TEACHER_2026_PT2_Q11_C",
                      def: "वर्ष :",
                    }),
                    dataIndex: "year",
                    key: "year_col",
                    render: (_, field, index) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q11_C"]}
                        style={{ margin: 0 }}
                      >
                        <DatePicker
                          disabled={disabled || isReviewMode}
                          picker="year"
                          format="YYYY"
                          disabledDate={(current) =>
                            current && current > dayjs().endOf("year")
                          }
                          placeholder={Text({
                            tid: "SELECT_YEAR",
                            def: "Select Year",
                          })}
                          style={{ width: "100%", minWidth: 120 }}
                          onChange={(date, dateString) =>
                            handleDateChange(
                              form,
                              "TEACHER_2026_PT2_Q11",
                              index,
                              Array.isArray(dateString)
                                ? dateString[0]
                                : dateString,
                              handleFieldChange,
                            )
                          }
                        />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({
                      tid: "UPLOAD_DOCUMENT",
                      def: "Upload Document",
                    }),
                    dataIndex: "document",
                    key: "document_col",
                    render: (_, field, index) => {
                      const assemblyValue = form.getFieldValue([
                        "TEACHER_2026_PT2_Q11",
                        index,
                        "TEACHER_2026_PT2_Q11_A",
                      ]);
                      return (
                        <Form.Item
                          {...field}
                          name={[field.name, "TEACHER_2026_PT2_Q11_DOC"]}
                          rules={
                            assemblyValue
                              ? [{ required: true, message: REQUIRED }]
                              : []
                          }
                          style={{ margin: 0 }}
                        >
                          <DocumentUploadTableField
                            formId={formId || ""}
                            userId={userId || ""}
                            labelName={`Assembly Certificate (Row ${index + 1})`}
                            disabled={disabled || isReviewMode}
                            existingDocumentUrl={form.getFieldValue([
                              "TEACHER_2026_PT2_Q11",
                              index,
                              "TEACHER_2026_PT2_Q11_DOC",
                            ])}
                            onUploadComplete={(docUrl) => {
                              form.setFieldValue(
                                [
                                  "TEACHER_2026_PT2_Q11",
                                  index,
                                  "TEACHER_2026_PT2_Q11_DOC",
                                ],
                                docUrl,
                              );
                              handleFieldChange(
                                "TEACHER_2026_PT2_Q11",
                                buildArrayPayload(
                                  form.getFieldValue("TEACHER_2026_PT2_Q11") ||
                                    [],
                                ),
                              );
                            }}
                            compact
                          />
                        </Form.Item>
                      );
                    },
                  },
                  {
                    title: Text({ tid: "ACTION", def: "Action" }),
                    key: "action",
                    width: 300,
                    render: (_, field, index) => (
                      <Space
                        style={{
                          display: "flex",
                          gap: "5px",
                          flexWrap: "wrap",
                        }}
                      >
                        {fields.length > 1 && (
                          <Button
                            disabled={disabled || isReviewMode}
                            variant="filled"
                            className="actionButton"
                            onClick={() => {
                              remove(field.name);
                              handleFieldChange(
                                "TEACHER_2026_PT2_Q11",
                                buildArrayPayload(
                                  form.getFieldValue("TEACHER_2026_PT2_Q11") ||
                                    [],
                                ),
                              );
                            }}
                          >
                            {REMOVE}
                          </Button>
                        )}
                        {index === fields.length - 1 && (
                          <Button
                            disabled={disabled || isReviewMode}
                            variant="filled"
                            className="actionButton"
                            onClick={() => {
                              add();
                              setTimeout(() => {
                                handleFieldChange(
                                  "TEACHER_2026_PT2_Q11",
                                  buildArrayPayload(
                                    form.getFieldValue(
                                      "TEACHER_2026_PT2_Q11",
                                    ) || [],
                                  ),
                                );
                              }, 0);
                            }}
                          >
                            {ADD}
                          </Button>
                        )}
                      </Space>
                    ),
                  },
                ];

                return (
                  <Table
                    dataSource={fields}
                    columns={columns}
                    pagination={false}
                    rowKey="key"
                    scroll={{ x: "max-content" }}
                  />
                );
              }}
            </Form.List>
          </Col>
        </Row>

        {/* 12. Vedic Shrauta Yagnas */}
        <Row className={styles.tableMargin}>
          <Col xs={24} className="onClickRenderFields">
            <Typography.Text>
              {Text({
                tid: "TEACHER_2026_PT2_Q12",
                def: "१२. वैदिक श्रौत यज्ञादि में आपकी गतिविधि :",
              })}
            </Typography.Text>
            <Form.List name="TEACHER_2026_PT2_Q12">
              {(fields, { add, remove }) => {
                const columns: TableProps<any>["columns"] = [
                  {
                    title: Text({
                      tid: "TEACHER_2026_PT2_Q12_A",
                      def: "यज्ञकार्य :",
                    }),
                    dataIndex: "ritual",
                    key: "ritual_col",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q12_A"]}
                        style={{ margin: 0 }}
                      >
                        <Input disabled={disabled || isReviewMode} />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({
                      tid: "TEACHER_2026_PT2_Q12_B",
                      def: "स्थान :",
                    }),
                    dataIndex: "place",
                    key: "place_col",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q12_B"]}
                        style={{ margin: 0 }}
                      >
                        <Input disabled={disabled || isReviewMode} />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({
                      tid: "TEACHER_2026_PT2_Q12_C",
                      def: "वर्ष :",
                    }),
                    dataIndex: "year",
                    key: "year_col",
                    render: (_, field, index) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q12_C"]}
                        style={{ margin: 0 }}
                      >
                        <DatePicker
                          disabled={disabled || isReviewMode}
                          picker="year"
                          format="YYYY"
                          disabledDate={(current) =>
                            current && current > dayjs().endOf("year")
                          }
                          placeholder={Text({
                            tid: "SELECT_YEAR",
                            def: "Select Year",
                          })}
                          style={{ width: "100%", minWidth: 120 }}
                          onChange={(date, dateString) =>
                            handleDateChange(
                              form,
                              "TEACHER_2026_PT2_Q12",
                              index,
                              Array.isArray(dateString)
                                ? dateString[0]
                                : dateString,
                              handleFieldChange,
                            )
                          }
                        />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({
                      tid: "UPLOAD_DOCUMENT",
                      def: "Upload Document",
                    }),
                    dataIndex: "document",
                    key: "document_col",
                    render: (_, field, index) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q12_DOC"]}
                      >
                        <DocumentUploadTableField
                          formId={formId || ""}
                          userId={userId || ""}
                          labelName={`Yagni Certificate (Row ${index + 1})`}
                          disabled={disabled || isReviewMode}
                          existingDocumentUrl={form.getFieldValue([
                            "TEACHER_2026_PT2_Q12",
                            index,
                            "TEACHER_2026_PT2_Q12_DOC",
                          ])}
                          onUploadComplete={(docUrl) => {
                            form.setFieldValue(
                              [
                                "TEACHER_2026_PT2_Q12",
                                index,
                                "TEACHER_2026_PT2_Q12_DOC",
                              ],
                              docUrl,
                            );
                            handleFieldChange(
                              "TEACHER_2026_PT2_Q12",
                              buildArrayPayload(
                                form.getFieldValue("TEACHER_2026_PT2_Q12") ||
                                  [],
                              ),
                            );
                          }}
                          compact
                        />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({ tid: "ACTION", def: "Action" }),
                    key: "action",
                    width: 300,
                    render: (_, field, index) => (
                      <Space
                        style={{
                          display: "flex",
                          gap: "5px",
                          flexWrap: "wrap",
                        }}
                      >
                        {fields.length > 1 && (
                          <Button
                            disabled={disabled || isReviewMode}
                            variant="filled"
                            className="actionButton"
                            onClick={() => {
                              remove(field.name);
                              handleFieldChange(
                                "TEACHER_2026_PT2_Q12",
                                buildArrayPayload(
                                  form.getFieldValue("TEACHER_2026_PT2_Q12") ||
                                    [],
                                ),
                              );
                            }}
                          >
                            {REMOVE}
                          </Button>
                        )}
                        {index === fields.length - 1 && (
                          <Button
                            disabled={disabled || isReviewMode}
                            variant="filled"
                            className="actionButton"
                            onClick={() => {
                              add();
                              setTimeout(() => {
                                handleFieldChange(
                                  "TEACHER_2026_PT2_Q12",
                                  buildArrayPayload(
                                    form.getFieldValue(
                                      "TEACHER_2026_PT2_Q12",
                                    ) || [],
                                  ),
                                );
                              }, 0);
                            }}
                          >
                            {ADD}
                          </Button>
                        )}
                      </Space>
                    ),
                  },
                ];

                return (
                  <Table
                    dataSource={fields}
                    columns={columns}
                    pagination={false}
                    rowKey="key"
                    scroll={{ x: "max-content" }}
                  />
                );
              }}
            </Form.List>
          </Col>
        </Row>

        {/* 13. आपको प्राप्त पुरस्कारों अथवा प्रशस्तिपत्रों की जानकारी */}
        <Row className={styles.tableMargin}>
          <Col xs={24} className="onClickRenderFields">
            <Typography.Text>
              {Text({
                tid: "TEACHER_2026_PT2_Q13",
                def: "१३. आपको प्राप्त पुरस्कारों अथवा प्रशस्तिपत्रों की जानकारी :",
              })}
            </Typography.Text>
            <Form.List name="TEACHER_2026_PT2_Q13">
              {(fields, { add, remove }) => {
                const columns: TableProps<any>["columns"] = [
                  {
                    title: Text({
                      tid: "TEACHER_2026_PT2_Q13_A",
                      def: "पुरस्कार का नाम",
                    }),
                    dataIndex: "award",
                    key: "award_col",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q13_A"]}
                        style={{ margin: 0 }}
                      >
                        <Input disabled={disabled || isReviewMode} />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({
                      tid: "TEACHER_2026_PT2_Q13_B",
                      def: "प्रदाता",
                    }),
                    dataIndex: "provider",
                    key: "provider_col",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q13_B"]}
                        style={{ margin: 0 }}
                      >
                        <Input disabled={disabled || isReviewMode} />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({
                      tid: "TEACHER_2026_PT2_Q13_C",
                      def: "वर्ष :",
                    }),
                    dataIndex: "year",
                    key: "year_col",
                    render: (_, field, index) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q13_C"]}
                        style={{ margin: 0 }}
                      >
                        <DatePicker
                          disabled={disabled || isReviewMode}
                          picker="year"
                          format="YYYY"
                          disabledDate={(current) =>
                            current && current > dayjs().endOf("year")
                          }
                          placeholder={Text({
                            tid: "SELECT_YEAR",
                            def: "Select Year",
                          })}
                          style={{ width: "100%", minWidth: 120 }}
                          onChange={(date, dateString) =>
                            handleDateChange(
                              form,
                              "TEACHER_2026_PT2_Q13",
                              index,
                              Array.isArray(dateString)
                                ? dateString[0]
                                : dateString,
                              handleFieldChange,
                            )
                          }
                        />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({
                      tid: "UPLOAD_DOCUMENT",
                      def: "Upload Document",
                    }),
                    dataIndex: "document",
                    key: "document_col",
                    render: (_, field, index) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q13_DOC"]}
                        style={{ margin: 0 }}
                      >
                        <DocumentUploadTableField
                          formId={formId || ""}
                          userId={userId || ""}
                          labelName={`Award Certificate (Row ${index + 1})`}
                          disabled={disabled || isReviewMode}
                          existingDocumentUrl={form.getFieldValue([
                            "TEACHER_2026_PT2_Q13",
                            index,
                            "TEACHER_2026_PT2_Q13_DOC",
                          ])}
                          onUploadComplete={(docUrl) => {
                            form.setFieldValue(
                              [
                                "TEACHER_2026_PT2_Q13",
                                index,
                                "TEACHER_2026_PT2_Q13_DOC",
                              ],
                              docUrl,
                            );
                            handleFieldChange(
                              "TEACHER_2026_PT2_Q13",
                              buildArrayPayload(
                                form.getFieldValue("TEACHER_2026_PT2_Q13") ||
                                  [],
                              ),
                            );
                          }}
                          compact
                        />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({ tid: "ACTION", def: "Action" }),
                    key: "action",
                    width: 300,
                    render: (_, field, index) => (
                      <Space
                        style={{
                          display: "flex",
                          gap: "5px",
                          flexWrap: "wrap",
                        }}
                      >
                        {fields.length > 1 && (
                          <Button
                            disabled={disabled || isReviewMode}
                            variant="filled"
                            className="actionButton"
                            onClick={() => {
                              remove(field.name);
                              handleFieldChange(
                                "TEACHER_2026_PT2_Q13",
                                buildArrayPayload(
                                  form.getFieldValue("TEACHER_2026_PT2_Q13") ||
                                    [],
                                ),
                              );
                            }}
                          >
                            {REMOVE}
                          </Button>
                        )}
                        {index === fields.length - 1 && (
                          <Button
                            disabled={disabled || isReviewMode}
                            variant="filled"
                            className="actionButton"
                            onClick={() => {
                              add();
                              setTimeout(() => {
                                handleFieldChange(
                                  "TEACHER_2026_PT2_Q13",
                                  buildArrayPayload(
                                    form.getFieldValue(
                                      "TEACHER_2026_PT2_Q13",
                                    ) || [],
                                  ),
                                );
                              }, 0);
                            }}
                          >
                            {ADD}
                          </Button>
                        )}
                      </Space>
                    ),
                  },
                ];

                return (
                  <Table
                    dataSource={fields}
                    columns={columns}
                    pagination={false}
                    rowKey="key"
                    scroll={{ x: "max-content" }}
                  />
                );
              }}
            </Form.List>
          </Col>
        </Row>

        {/* 14. आपके द्वारा स्वलिखित ग्रंथ, लेख, शोधपत्र प्रकाशन की जानकारी : */}
        <Row className={styles.tableMargin}>
          <Col xs={24} className="onClickRenderFields">
            <Typography.Text>
              {Text({
                tid: "TEACHER_2026_PT2_Q14",
                def: "१४. आपके द्वारा स्वलिखित ग्रंथ, लेख, शोधपत्र प्रकाशन की जानकारी :",
              })}
            </Typography.Text>
            <Form.List name="TEACHER_2026_PT2_Q14">
              {(fields, { add, remove }) => {
                const columns: TableProps<any>["columns"] = [
                  {
                    title: Text({
                      tid: "TEACHER_2026_PT2_Q14_A",
                      def: "शीर्षक",
                    }),
                    dataIndex: "title",
                    key: "title_col",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q14_A"]}
                        style={{ margin: 0 }}
                      >
                        <Input disabled={disabled || isReviewMode} />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({
                      tid: "TEACHER_2026_PT2_Q14_B",
                      def: "प्रकाशक",
                    }),
                    dataIndex: "publisher",
                    key: "publisher_col",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q14_B"]}
                        style={{ margin: 0 }}
                      >
                        <Input disabled={disabled || isReviewMode} />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({ tid: "TEACHER_2026_PT2_Q14_C", def: "ISBN" }),
                    dataIndex: "isbn",
                    key: "isbn_col",
                    render: (_, field) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q14_C"]}
                        style={{ margin: 0 }}
                      >
                        <Input disabled={disabled || isReviewMode} />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({
                      tid: "TEACHER_2026_PT2_Q14_D",
                      def: "वर्ष :",
                    }),
                    dataIndex: "year",
                    key: "year_col",
                    render: (_, field, index) => (
                      <Form.Item
                        {...field}
                        name={[field.name, "TEACHER_2026_PT2_Q14_D"]}
                        style={{ margin: 0 }}
                      >
                        <DatePicker
                          disabled={disabled || isReviewMode}
                          picker="year"
                          format="YYYY"
                          disabledDate={(current) =>
                            current && current > dayjs().endOf("year")
                          }
                          placeholder={Text({
                            tid: "SELECT_YEAR",
                            def: "Select Year",
                          })}
                          style={{ width: "100%", minWidth: 120 }}
                          onChange={(date, dateString) =>
                            handleDateChange(
                              form,
                              "TEACHER_2026_PT2_Q14",
                              index,
                              Array.isArray(dateString)
                                ? dateString[0]
                                : dateString,
                              handleFieldChange,
                            )
                          }
                        />
                      </Form.Item>
                    ),
                  },
                  {
                    title: Text({
                      tid: "UPLOAD_DOCUMENT",
                      def: "Upload Document",
                    }),
                    dataIndex: "document",
                    key: "document_col",
                    render: (_, field, index) => {
                      const titleValue = form.getFieldValue([
                        "TEACHER_2026_PT2_Q14",
                        index,
                        "TEACHER_2026_PT2_Q14_A",
                      ]);
                      return (
                        <Form.Item
                          {...field}
                          name={[field.name, "TEACHER_2026_PT2_Q14_DOC"]}
                          // rules={
                          //   titleValue
                          //     ? [{ required: true, message: REQUIRED }]
                          //     : []
                          // }
                          style={{ margin: 0 }}
                        >
                          <DocumentUploadTableField
                            formId={formId || ""}
                            userId={userId || ""}
                            labelName={`Publication Evidence (Row ${index + 1})`}
                            disabled={disabled || isReviewMode}
                            existingDocumentUrl={form.getFieldValue([
                              "TEACHER_2026_PT2_Q14",
                              index,
                              "TEACHER_2026_PT2_Q14_DOC",
                            ])}
                            onUploadComplete={(docUrl) => {
                              form.setFieldValue(
                                [
                                  "TEACHER_2026_PT2_Q14",
                                  index,
                                  "TEACHER_2026_PT2_Q14_DOC",
                                ],
                                docUrl,
                              );
                              handleFieldChange(
                                "TEACHER_2026_PT2_Q14",
                                buildArrayPayload(
                                  form.getFieldValue("TEACHER_2026_PT2_Q14") ||
                                    [],
                                ),
                              );
                            }}
                            compact
                          />
                        </Form.Item>
                      );
                    },
                  },
                  {
                    title: Text({ tid: "ACTION", def: "Action" }),
                    key: "action",
                    width: 300,
                    render: (_, field, index) => (
                      <Space
                        style={{
                          display: "flex",
                          gap: "5px",
                          flexWrap: "wrap",
                        }}
                      >
                        {fields.length > 1 && (
                          <Button
                            disabled={disabled || isReviewMode}
                            variant="filled"
                            className="actionButton"
                            onClick={() => {
                              remove(field.name);
                              handleFieldChange(
                                "TEACHER_2026_PT2_Q14",
                                buildArrayPayload(
                                  form.getFieldValue("TEACHER_2026_PT2_Q14") ||
                                    [],
                                ),
                              );
                            }}
                          >
                            {REMOVE}
                          </Button>
                        )}
                        {index === fields.length - 1 && (
                          <Button
                            disabled={disabled || isReviewMode}
                            variant="filled"
                            className="actionButton"
                            onClick={() => {
                              add();
                              setTimeout(() => {
                                handleFieldChange(
                                  "TEACHER_2026_PT2_Q14",
                                  buildArrayPayload(
                                    form.getFieldValue(
                                      "TEACHER_2026_PT2_Q14",
                                    ) || [],
                                  ),
                                );
                              }, 0);
                            }}
                          >
                            {ADD}
                          </Button>
                        )}
                      </Space>
                    ),
                  },
                ];

                return (
                  <Table
                    dataSource={fields}
                    columns={columns}
                    pagination={false}
                    rowKey="key"
                    scroll={{ x: "max-content" }}
                  />
                );
              }}
            </Form.List>
          </Col>
        </Row>

        {/* 15. Attached Documents Summary */}
        <Row className={styles.tableMargin}>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "TEACHER_2026_PT2_Q15",
                def: "१५. इस आवेदन से संलग्न आलेखों का विवरण :",
              })}
              <FormHelpTooltip
                title={Text({
                  tid: "TEACHER_2026_PT2_Q15_TOOLTIP",
                  def: "Summary of all documents uploaded in this form.",
                })}
              />
            </Typography.Text>
          </Col>
        </Row>
        <Row className="mb-20">
          <Col xs={24}>
            <Form.Item name="TEACHER_2026_PT2_Q15">
              <Table
                columns={TABLE_COLUMNS_TEACHER_2026_PT2_Q15}
                dataSource={form.getFieldValue("TEACHER_2026_PT2_Q15") || []}
                pagination={false}
                style={{ width: "100%" }}
                rowKey={(record, index) =>
                  index !== undefined ? index.toString() : ""
                }
              />
            </Form.Item>
          </Col>
        </Row>

        {/* 16. Applicant's Affidavit */}
        <div
          id="teacher_form2_applicant_affidavit"
          className="affidavitSection"
          style={{ marginTop: 20 }}
        >
          <Row>
            <Col>
              <Typography.Text>
                {Text({
                  tid: "TEACHER_AFFIDAVIT_TITLE",
                  def: "१६. आवेदक का शपथ पत्र :",
                })}
              </Typography.Text>
            </Col>
          </Row>
          <Row className="affidavitRow">
            <Col xs={24}>
              <Typography.Text
                type="secondary"
                style={{ maxWidth: "max-content" }}
              >
                {Text({
                  tid: "TEACHER_2026_P2_AFFIDAVIT_I",
                  def: "मैं (आवेदक का पूरा नाम)",
                })}
              </Typography.Text>
              <Form.Item
                name="TEACHER_2026_PT2_Q16"
                className="affidavitRowInput"
                rules={[{ required: true, message: REQUIRED }]}
              >
                <Input
                  id={applyPdfStyles ? "affidavit_input" : ""}
                  disabled={true}
                />
              </Form.Item>
              <Typography.Text
                type="secondary"
                style={{ maxWidth: "max-content", marginBottom: "0" }}
              >
                {Text({
                  tid: "TEACHER_2026_P2_AFFIDAVIT_LINE_1",
                  def: "एतद्द्वारा सत्यनिष्ठा से यह प्रमाणित करता हूँ कि:",
                })}
              </Typography.Text>
            </Col>
          </Row>
          <Row>
            <Col xs={24}>
              <ul>
                <li>
                  <Typography.Text type="secondary">
                    {Text({
                      tid: "TEACHER_2026_P2_AFFIDAVIT_CLAUSE_1",
                      def: "मैं वेद विद्या का वर्तमान में पूर्णकालिक वेदाध्यापक हूँ:",
                    })}
                  </Typography.Text>
                </li>
                <li>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "10px",
                    }}
                  >
                    {Text({
                      tid: "TEACHER_2026_P2_AFFIDAVIT_CLAUSE_21",
                      def: "मेरा पूर्णकालिक वेदाध्यापन विगत",
                    })}

                    <Form.Item
                      name="TEACHER_2026_PT2_Q17_YEARS"
                      className="affidavitRowInput"
                      rules={[{ required: true, message: REQUIRED }]}
                    >
                      <Input
                        disabled={disabled || isReviewMode}
                        type="number"
                        min={0}
                        placeholder="00"
                      />
                    </Form.Item>

                    {Text({
                      tid: "TEACHER_2026_P2_AFFIDAVIT_CLAUSE_22",
                      def: "वर्षों से निरन्तर चल रहा है;",
                    })}
                  </div>
                </li>
                <li>
                  <Typography.Text type="secondary">
                    {Text({
                      tid: "TEACHER_2026_P2_AFFIDAVIT_CLAUSE_3",
                      def: "इस आवेदन पत्र में दी गई सभी सूचनाएँ पूर्णतः सत्य हैं;",
                    })}
                  </Typography.Text>
                </li>
                <li>
                  <Typography.Text type="secondary">
                    {Text({
                      tid: "TEACHER_2026_P2_AFFIDAVIT_CLAUSE_4",
                      def: "इस आवेदन से संलग्न सभी अनुबन्धों व प्रमाण पत्रों में दी गई जानकारी सत्य है।",
                    })}
                  </Typography.Text>
                </li>
              </ul>
              <div>
                <Typography.Text type="secondary">
                  {Text({
                    tid: "TEACHER_2026_P2_AFFIDAVIT_VERIFICATION",
                    def: "मैंने सभी तथ्यों की पुष्टि करने के बाद इस प्रमाण पत्र पर हस्ताक्षर किये हैं।",
                  })}
                </Typography.Text>
                <br />
                <Typography.Text type="secondary">
                  {Text({
                    tid: "TEACHER_2026_P2_AFFIDAVIT_UNDERSTANDING",
                    def: "मैं इस शपथपत्र पर हस्ताक्षर करने के महत्व व निहितार्थ को पूरी तरह से समझता हूँ।",
                  })}
                </Typography.Text>
                <br />
                {!disabled ? (
                  <Form.Item
                    name="affidavitAccepted"
                    valuePropName="checked"
                    rules={[
                      {
                        required: true,
                        transform: (value: boolean) => value || undefined,
                        type: "boolean",
                        message: Text({
                          tid: "AFFIDAVIT_ACCEPTANCE_REQUIRED",
                          def: "You must accept the affidavit declaration to submit the form",
                        }),
                      },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <Checkbox
                      checked={affidavitAccepted}
                      disabled={disabled || isReviewMode}
                    >
                      {Text({
                        tid: "TEACHER_2026_P2_AFFIDAVIT_ACCEPTANCE",
                        def: "मैं स्वीकार करता हूँ कि यदि यह शपथ पत्र असत्य पाया गया तो मैं व मेरे सभी वेदविद्यार्थी सदा के लिये भारतात्मा पुरस्कार के लिये अयोग्य माने जा सकते हैं।",
                      })}
                    </Checkbox>
                  </Form.Item>
                ) : (
                  <Typography.Text type="secondary">
                    {Text({
                      tid: "TEACHER_2026_P2_AFFIDAVIT_ACCEPTANCE",
                      def: "मैं स्वीकार करता हूँ कि यदि यह शपथ पत्र असत्य पाया गया तो मैं व मेरे सभी वेदविद्यार्थी सदा के लिये भारतात्मा पुरस्कार के लिये अयोग्य माने जा सकते हैं।",
                    })}
                  </Typography.Text>
                )}
              </div>
            </Col>
          </Row>

          {/* SIGNATURE, NAME, PLACE, DATE */}
          <Row className="QuestionsSubSection affidavitQuestionFirst">
            <Col xs={24} lg={12} xl={8}>
              <DocumentUploadField
                fieldName="TEACHER_2026_PT2_Q18"
                label={Text({
                  tid: "TEACHER_2026_P2_AFFIDAVIT_SIGNATURE_LABEL",
                  def: "Applicant's Signature (Upload Image)",
                })}
                formId={formId || ""}
                userId={user?.id || ""}
                currentStage={eFORMSTATUS.STAGE2INPROGRESS}
                disabled={disabled || isReviewMode}
                existingDocuments={documentsData}
                onUploadComplete={(docId) =>
                  handleFieldChange(
                    "TEACHER_2026_PT2_Q18",
                    docId,
                    eAnswerType.FILE,
                  )
                }
              />
            </Col>
          </Row>

          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="TEACHER_2026_PT2_Q19"
                label={Text({
                  tid: "TEACHER_2026_P2_AFFIDAVIT_NAME_LABEL",
                  def: "Applicant's Full Name: (Auto-filled)",
                })}
                rules={[{ required: true, message: REQUIRED }]}
              >
                <Input
                  disabled={true}
                  id={applyPdfStyles ? "affidavit_input" : ""}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="TEACHER_2026_PT2_Q20"
                label={Text({
                  tid: "TEACHER_2026_P2_AFFIDAVIT_PLACE_LABEL",
                  def: "Place* : e.g. Udaipur",
                })}
                rules={[{ required: true, message: REQUIRED }]}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  id={applyPdfStyles ? "affidavit_input" : ""}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="TEACHER_2026_PT2_Q21"
                label={Text({
                  tid: "TEACHER_2026_P2_AFFIDAVIT_DATE_LABEL",
                  def: "Date: ",
                })}
              >
                <DatePicker
                  disabled={disabled || isReviewMode}
                  style={{ width: "100%" }}
                  format="DD-MM-YYYY"
                  placeholder="DD-MM-YYYY"
                  defaultValue={dayjs()}
                  id={applyPdfStyles ? "affidavit_input" : ""}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* 16. Institution Head's Affidavit */}
        <Row className="QuestionsSubSection" style={{ marginTop: "30px" }}>
          <Col xs={24}>
            <Typography.Text>
              {Text({
                tid: "INSTITUTION_HEAD_AFFIDAVIT",
                def: "१७. संस्था प्रमुख का शपथ पत्र :",
              })}
            </Typography.Text>
            <br />
            <Typography.Text type="secondary">
              {Text({
                tid: "INSTITUTION_AFFIDAVIT_REQUIRED",
                def: "यदि आप संस्था द्वारा सञ्चालित वेदविद्यालय में वेदाध्यापन कर रहे हैं तब संस्था प्रमुख का शपथ पत्र आवश्यक है।",
              })}
            </Typography.Text>
          </Col>
        </Row>

        {!disabled && (
          <div className="affidavitSection">
            <Row className="mb-15 QuestionsSubSection">
              <Space wrap>
                <Button
                  type="default"
                  onClick={() =>
                    handleGeneratePDF(
                      "teacher_form2_institution_head_affidavit",
                      "teacher_institution_head_affidavit",
                    )
                  }
                  className="downloadAffidavitButton"
                >
                  {Text({
                    tid: "DOWNLOAD_INSTITUTION_HEAD_AFFIDAVIT",
                    def: "Download Institution Head Affidavit",
                  })}
                </Button>
              </Space>
            </Row>
          </div>
        )}

        {/* HIDDEN PDF LAYOUT FOR HEAD AFFIDAVIT */}
        <div
          id="teacher_form2_institution_head_affidavit"
          className="affidavitSection"
          style={{ display: applyPdfStyles ? "block" : "none" }}
        >
          <Row style={{ marginBottom: "5px" }}>
            <Col>
              <Typography.Text strong>
                {Text({
                  tid: "INSTITUTION_HEAD_AFFIDAVIT_TITLE",
                  def: "संस्था प्रमुख का शपथ पत्र :",
                })}
              </Typography.Text>
            </Col>
          </Row>
          <Row className="affidavitRow">
            <Col xs={24}>
              <Typography.Text>
                {Text({
                  tid: "INSTITUTION_HEAD_AFFIDAVIT_DESC",
                  def: "एतद्द्वारा यह प्रमाणित किया जाता है कि आवेदक इस संस्था में वेदाध्यापक हैं और उपर्युक्त सम्पूर्ण जानकारी सर्वथा सत्य है।",
                })}
              </Typography.Text>
            </Col>
          </Row>
          <br />
          <br />

          <Row className="QuestionsSubSection affidavitQuestionFirst">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="TEACHER_2026_PT2_Q17_HEAD_SIGN"
                label={Text({
                  tid: "INSTITUTION_HEAD_SIGNATURE",
                  def: "संस्थाप्रमुख के हस्ताक्षर :",
                })}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  id={applyPdfStyles ? "affidavit_input" : ""}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="TEACHER_2026_PT2_Q17_HEAD_NAME"
                label={Text({
                  tid: "INSTITUTION_HEAD_NAME",
                  def: "संस्थाप्रमुख का नाम :",
                })}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  id={applyPdfStyles ? "affidavit_input" : ""}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="TEACHER_2026_PT2_Q17_SCHOOL_NAME"
                label={Text({
                  tid: "VEDA_VIDYALAYA_NAME",
                  def: "वेदविद्यालय का नाम :",
                })}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  id={applyPdfStyles ? "affidavit_input" : ""}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="TEACHER_2026_PT2_Q17_PLACE"
                label={Text({
                  tid: "PLACE_LABEL_HINDI",
                  def: "स्थान:",
                })}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  id={applyPdfStyles ? "affidavit_input" : ""}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={8}>
              <Form.Item
                name="TEACHER_2026_PT2_Q17_DATE"
                label={Text({
                  tid: "DATE_LABEL_HINDI",
                  def: "दिनांक:",
                })}
              >
                <Input
                  disabled={disabled || isReviewMode}
                  id={applyPdfStyles ? "affidavit_input" : ""}
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div className="affidavitSection" style={{ marginBottom: "25px" }}>
          <Row className="QuestionsSubSection">
            <Col xs={24} lg={12} xl={8}>
              <DocumentUploadField
                fieldName="TEACHER_2026_PT2_Q22"
                label={Text({
                  tid: "UPLOAD_INSTITUTION_HEAD_AFFIDAVIT",
                  def: "Upload Institution Head Affidavit",
                })}
                formId={formId || ""}
                userId={user?.id || ""}
                currentStage={eFORMSTATUS.STAGE2INPROGRESS}
                disabled={disabled || isReviewMode}
                existingDocuments={documentsData}
                onUploadComplete={(docId) =>
                  handleFieldChange(
                    "TEACHER_2026_PT2_Q22",
                    docId,
                    eAnswerType.FILE,
                  )
                }
              />
            </Col>
          </Row>
        </div>

        <Row
          style={
            disabled === true
              ? { marginTop: "var(--bs-item-negative-margin-30)" }
              : undefined
          }
        >
          {disabled === false && !isReviewMode && (
            <Button
              block
              type="primary"
              onClick={handleNextClick}
              disabled={isLoading || !affidavitAccepted}
              className="formSubmitButton"
            >
              {Text({ tid: "NEXT", def: "Next" })}
            </Button>
          )}
          {disabled === false && isReviewMode && (
            <div style={{ display: "flex", gap: "16px", width: "100%" }}>
              <Button
                block
                onClick={handleBackClick}
                disabled={isLoading}
                className="formSubmitButton"
              >
                {Text({ tid: "BACK", def: "Back" })}
              </Button>
              <Button
                block
                type="primary"
                htmlType="submit"
                disabled={isLoading || !affidavitAccepted}
                className="formSubmitButton"
                loading={isSubmitting}
              >
                {Text({ tid: "SUBMIT", def: "Submit" })}
              </Button>
            </div>
          )}
        </Row>
      </Form>
    </div>
  );
};
export default TeacherPartTwo;
